import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import axios from 'axios';
import ApiKeysTab from '../components/ApiManagement/ApiKeysTab';
import ApiStatsTab from '../components/ApiManagement/ApiStatsTab';
import RateLimitsTab from '../components/ApiManagement/RateLimitsTab';
import MobileVersionsTab from '../components/ApiManagement/MobileVersionsTab';
import ApiReferenceTab from '../components/ApiManagement/ApiReferenceTab';
import ApiTesterTab from '../components/ApiManagement/ApiTesterTab';
import WebhookLogsTab from '../components/ApiManagement/WebhookLogsTab';
import CompanyList from './Companies/CompanyList';
import CompanyDetail from './Companies/CompanyDetail';
import CallCenterBoard from './CallCenter/CallCenterBoard';
import AdminAICenterPanel from '../components/AdminAICenter/AdminAICenterPanel';
import ReportsView from '../components/Reports/ReportsView';
import ADMIN_PAGES from '../data/adminPages';
import BrandSettingsPanel from '../components/Admin/BrandSettingsPanel';
import BrandAssignSelect from '../components/Admin/BrandAssignSelect';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement,
  Tooltip, Legend, Filler
);

// Module definitions: key -> { label, industries: null = all, or array of specific industries }
const MODULE_DEFINITIONS = {
  // Ortak modüller (tüm sektörler)
  calendar:          { label: 'Takvim / Randevu',      industries: null },
  customers:         { label: 'Müşteriler',              industries: null },
  pos:               { label: 'POS / Kasa',             industries: null },
  products:          { label: 'Ürünler / Stok',         industries: null },
  settings:          { label: 'Ayarlar',                 industries: null },
  ai:                { label: 'AI Merkezi',              industries: null },
  metaAds:           { label: 'Meta Reklamları',         industries: null },
  integrations:      { label: 'Entegrasyonlar (Instagram vb.)', industries: null },
  preConsultations:  { label: 'Ön Görüşmeler',          industries: null },
  kanban:            { label: 'Satış Hunisi (Kanban)',  industries: null },
  finance:           { label: 'Finans & Vergi',         industries: null },
  inventoryAlerts:   { label: 'Stok Tedarik Uyarısı',   industries: null },
  automations:       { label: 'Mesaj Otomasyonları',    industries: null },
  messages:          { label: 'Mesajlaşma (Sosyal Medya)', industries: null },
  campaigns:         { label: 'Kampanyalar (Toplu Mesaj)', industries: null },
  reports:           { label: 'Raporlar',                  industries: null },
  calls:             { label: 'Çağrılar',                  industries: null },
  announcements:     { label: 'Güncellemeler',              industries: null },
  notifications:     { label: 'Bildirim Yönetimi',       industries: null },
  companies:         { label: 'Firma Yönetim Ekranı',    industries: null },
  opportunities:     { label: 'Gelir Fırsatları',       industries: null },
  // Güzellik Salonu
  staff:             { label: 'Personel Yönetimi',      industries: ['beauty_salon'] },
  services:          { label: 'Hizmetler',               industries: ['beauty_salon'] },
  // Gelinlikçi
  measurements:      { label: 'Ölçü Takibi',            industries: ['bridal_shop'] },
  fitting:           { label: 'Prova Takvimi',           industries: ['bridal_shop'] },
  rental:            { label: 'Kira Sözleşmeleri',       industries: ['bridal_shop'] },
  // Duşakabin
  surveys:           { label: 'Keşif Anketleri',        industries: ['dusakabin'] },
  quotes:            { label: 'Teklif Yönetimi',         industries: ['dusakabin'] },
  production:        { label: 'Üretim Takibi',           industries: ['dusakabin'] },
  // Sağlık Turizmi
  patientTracking:   { label: 'Hasta Takibi',          industries: ['health_tourism'] },
  // Otelcilik
  hotelReservations: { label: 'Otel Rezervasyonları',  industries: ['hotel'] },
  roomManagement:    { label: 'Oda Yönetimi',          industries: ['hotel'] },
  // Diş Hekimliği
  dentalTreatments:  { label: 'Tedavi Takibi',         industries: ['dental_clinic'] },
  // Klinik & Hastane
  departments:       { label: 'Departmanlar',          industries: ['clinic'] },
  patientSatisfaction: { label: 'Hasta Memnuniyeti',    industries: ['clinic'] },
  // Ortak (Diş & Klinik)
  autoReminders:     { label: 'Otomatik Hatırlatma',   industries: ['dental_clinic', 'clinic'] },
  // E-ticaret
  ecommerceOrders:   { label: 'Siparişler',            industries: ['ecommerce'] },
  returnManagement:  { label: 'İade Yönetimi',         industries: ['ecommerce'] },
  // Oto Servis & Otomotiv
  vehicleTracking:   { label: 'Araç & İş Emirleri',     industries: ['automotive'] },
  partsInventory:    { label: 'Yedek Parça Stok',       industries: ['automotive'] },
  // Veteriner & Hasta Pet Takip
  petTracking:       { label: 'Pet Hasta Kartları',     industries: ['veterinary'] },
  vaccineCalendar:   { label: 'Aşı Takvimi',            industries: ['veterinary'] },
};

function AdminPanel() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  // Sayfa yenilenince (refresh) en son açık olan sekmede kalsın diye - izinli
  // olup olmadığı zaten aşağıdaki useEffect'te (satır ~183) kontrol ediliyor.
  const [activeTab, setActiveTab] = useState(() => localStorage.getItem('admin_active_tab') || 'dashboard');
  const [moreNavOpen, setMoreNavOpen] = useState(false);
  const [dashboardData, setDashboardData] = useState(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [registrations, setRegistrations] = useState([]);
  const [branchRequests, setBranchRequests] = useState([]);
  const [approvedBusinesses, setApprovedBusinesses] = useState([]);
  // Firmaların marka ataması: bu adminin seçebileceği markalar (bkz. /api/admin/my-brand-access)
  const [brandAccess, setBrandAccess] = useState({ scoped: false, assignable: [], editable: [] });
  const [firmBrandFilter, setFirmBrandFilter] = useState('');
  const [approvedBranches, setApprovedBranches] = useState([]);

  // Firma abonelik/ödeme takibi
  const [subscriptions, setSubscriptions] = useState([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [subscriptionEditModal, setSubscriptionEditModal] = useState(null); // { branch_id, branch_name, ...mevcut degerler }
  const [subscriptionPaymentModal, setSubscriptionPaymentModal] = useState(null); // { branch_id, branch_name }
  const [subscriptionHistoryBranch, setSubscriptionHistoryBranch] = useState(null); // { branch_id, branch_name }
  const [subscriptionPayments, setSubscriptionPayments] = useState([]);
  const [industrySettings, setIndustrySettings] = useState([]);
  const [newIndustryModal, setNewIndustryModal] = useState(false);
  const [industryForm, setIndustryForm] = useState({ industry: '', display_name: '', description: '', modules: {} });
  const [selectedIndustry, setSelectedIndustry] = useState(null);
  const [rejectModal, setRejectModal] = useState({ show: false, id: null, type: '' });
  const [editBranchModal, setEditBranchModal] = useState({ show: false, branch: null });
  const [editForm, setEditForm] = useState({ name: '', city: '', address: '', industry: '' });
  const [firmSearch, setFirmSearch] = useState('');
  const [aiOverview, setAiOverview] = useState([]);
  const [selectedAIBranch, setSelectedAIBranch] = useState(null);
  const [aiBranchAgents, setAiBranchAgents] = useState([]);
  const [aiBranchCalls, setAiBranchCalls] = useState([]);
  const [aiBranchSettings, setAiBranchSettings] = useState(null);
  const [aiBranchStats, setAiBranchStats] = useState(null);
  const [aiCenterTab, setAiCenterTab] = useState('agents'); // agents | settings | calls
  const [reportsData, setReportsData] = useState(null);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsPeriod, setReportsPeriod] = useState('week');
  const [selectedReportBranch, setSelectedReportBranch] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [announcementsLoading, setAnnouncementsLoading] = useState(false);
  const [announcementModal, setAnnouncementModal] = useState({ show: false, item: null });
  const [announcementForm, setAnnouncementForm] = useState({ title: '', content: '', type: 'update' });
  const [announcementSaving, setAnnouncementSaving] = useState(false);
  const [aiSettingsModal, setAiSettingsModal] = useState({ show: false, branch: null });
  const [aiSettingsForm, setAiSettingsForm] = useState({ provider: 'openai', model: 'gpt-4o', gemini_model: 'gemini-2.0-flash', openai_api_key: '', gemini_api_key: '', system_prompt: '', confidence_threshold: 85, max_history: 20, language: 'tr' });
  const [agentModal, setAgentModal] = useState({ show: false, agent: null });
  const [agentForm, setAgentForm] = useState({ name: '', type: 'voice', language: 'tr', language_label: 'Türkçe', working_hours: '7/24', status: 'active' });
  const [aiSaving, setAiSaving] = useState(false);
  const [apiTab, setApiTab] = useState('keys');
  const [selectedPhoneItem, setSelectedPhoneItem] = useState(null);
  const [selectedPhoneType, setSelectedPhoneType] = useState('call'); // 'call' | 'msg'
  const [selectedCustomCompanyId, setSelectedCustomCompanyId] = useState(null);

  // Demo Hesap Oluşturma
  const [createDemoModal, setCreateDemoModal] = useState(false);
  const [selectedDemoIndustry, setSelectedDemoIndustry] = useState('beauty_salon');
  const [demoCreating, setDemoCreating] = useState(false);
  const [demoResultModal, setDemoResultModal] = useState(null);

  const handleCreateDemoAccount = async () => {
    if (!selectedDemoIndustry) return;
    setDemoCreating(true);
    try {
      const res = await fetch('/api/admin/create-demo-account', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ industry: selectedDemoIndustry })
      });
      const data = await res.json();
      if (data.success) {
        setCreateDemoModal(false);
        setDemoResultModal(data);
        fetchData();
      } else {
        alert(data.error || 'Demo hesap oluşturulamadı.');
      }
    } catch (e) {
      console.error(e);
      alert('Sunucu hatası: ' + e.message);
    } finally {
      setDemoCreating(false);
    }
  };

  // Ritsa Asist - tüm işletme sahiplerinin yazdığı ORTAK/PAYLAŞIMLI WAHA bağlantısı
  const [ritsaAsistWaha, setRitsaAsistWaha] = useState({ waha_url: '', waha_session: 'default', waha_api_key_set: false });
  const [ritsaAsistWahaForm, setRitsaAsistWahaForm] = useState({ waha_url: '', waha_api_key: '', waha_session: 'default' });
  const [ritsaAsistWahaLoading, setRitsaAsistWahaLoading] = useState(false);
  const [ritsaAsistWahaSaving, setRitsaAsistWahaSaving] = useState(false);
  const [ritsaAsistWahaLoaded, setRitsaAsistWahaLoaded] = useState(false);


  const [industryAutomations, setIndustryAutomations] = useState({});
  const [loadingAutomations, setLoadingAutomations] = useState(false);

  // Sektör bazlı AI prompt standardı
  const [industryPromptData, setIndustryPromptData] = useState({});   // industry -> detay
  const [industryPromptText, setIndustryPromptText] = useState('');
  const [industryPromptChannel, setIndustryPromptChannel] = useState(''); // '' = genel, yoksa whatsapp/instagram/web_chat/sms
  const [industryPromptDirty, setIndustryPromptDirty] = useState(false);
  const [industryPromptSaving, setIndustryPromptSaving] = useState(false);
  const [industryPromptHistory, setIndustryPromptHistory] = useState([]);
  const [showPromptHistory, setShowPromptHistory] = useState(false);
  const [industryBranches, setIndustryBranches] = useState([]);
  const [previewBranchId, setPreviewBranchId] = useState('');
  const [promptPreview, setPromptPreview] = useState(null);
  const [automationTemplateModal, setAutomationTemplateModal] = useState({ show: false, template: null, industry: '' });
  const [automationTemplateForm, setAutomationTemplateForm] = useState({ name: '', trigger_type: 'appointment_before', trigger_value: 24, channel: 'whatsapp', template: '' });

  // Admin Kullanıcı Yönetimi State'leri
  const [adminUsers, setAdminUsers] = useState([]);
  const [adminUsersLoading, setAdminUsersLoading] = useState(false);
  const [adminUserModal, setAdminUserModal] = useState({ show: false, user: null });
  const [adminUserForm, setAdminUserForm] = useState({ username: '', password: '', email: '', role: 'admin', permissions: [] });

  // Giriş yapmış admin'in kendi rol/izinleri - hangi sekmeleri görebileceğini belirler
  const [currentAdmin] = useState(() => {
    try { return JSON.parse(localStorage.getItem('admin_user') || 'null'); } catch { return null; }
  });

  // permissions dizisi hem sayfa anahtarlarını hem de `brand:<key>` marka erişim
  // girdilerini tutar (bkz. server/routes/admin.js allowedBrandKeys). Sayfa
  // kontrolünde marka girdileri sayılmaz - yalnızca marka seçilmiş bir admin
  // "hiç sayfa seçilmedi = tüm sayfalar" kuralından düşmesin.
  const isPageEnabled = (pageKey) => {
    if (!currentAdmin) return true; // eski oturumlar (özellik öncesi) kilitlenmesin
    if (currentAdmin.role === 'super_admin') return true;
    const perms = Array.isArray(currentAdmin.permissions) ? currentAdmin.permissions : [];
    const pagePerms = perms.filter(p => !String(p).startsWith('brand:'));
    // Marka Ayarları: tam yetkili (izin listesi boş) adminler; kısıtlı adminde en az bir
    // marka atanmış olmalı VE 'brands' sayfası seçili (ya da hiç sayfa seçilmemiş) olmalı -
    // bkz. server/utils/adminBrandScope.js editableBrandKeys.
    if (pageKey === 'brands') {
      if (perms.length === 0) return true;
      return perms.some(p => String(p).startsWith('brand:')) && (pagePerms.length === 0 || pagePerms.includes('brands'));
    }
    if (pagePerms.length === 0) return true;
    return pagePerms.includes(pageKey);
  };

  useEffect(() => {
    if (!token) {
      navigate('/yonetim');
      return;
    }
    fetchData();
  }, [token]);

  useEffect(() => {
    localStorage.setItem('admin_active_tab', activeTab);
  }, [activeTab]);

  // Yönetim paneli mobil uygulamaya webview ile gömülecek - pinch-zoom/çift
  // dokunma zoom'u native bir app'te olmadığı gibi burada da olmamalı. Bu SADECE
  // admin paneli mount'luyken devrede - tenant CRM'in normal tarayıcı erişilebilirliği
  // (pinch-zoom) etkilenmesin diye component unmount olunca eski değere dönüyor.
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]');
    const previous = meta?.getAttribute('content');
    if (meta) meta.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover');
    return () => { if (meta && previous) meta.setAttribute('content', previous); };
  }, []);


  // Varsayılan sekme (dashboard) bu admin için kapalıysa, izinli ilk sekmeye geç
  useEffect(() => {
    if (!isPageEnabled(activeTab)) {
      const firstEnabled = ADMIN_PAGES.find(p => isPageEnabled(p.key));
      if (firstEnabled) setActiveTab(firstEnabled.key);
    }
    // eslint-disable-next-line
  }, [currentAdmin]);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [regRes, branchRes, bizRes, branchesRes, settingsRes, dbRes] = await Promise.all([
        axios.get('/api/admin/pending-registrations', { headers }),
        axios.get('/api/admin/pending-branches', { headers }),
        axios.get('/api/admin/approved-businesses', { headers }),
        axios.get('/api/admin/approved-branches', { headers }),
        axios.get('/api/admin/industry-settings', { headers }),
        axios.get('/api/admin/dashboard', { headers }).catch(() => ({ data: null }))
      ]);
      setRegistrations(Array.isArray(regRes.data) ? regRes.data : []);
      setBranchRequests(Array.isArray(branchRes.data) ? branchRes.data : []);
      setApprovedBusinesses(Array.isArray(bizRes.data) ? bizRes.data : []);
      setApprovedBranches(Array.isArray(branchesRes.data) ? branchesRes.data : []);
      setIndustrySettings(Array.isArray(settingsRes.data) ? settingsRes.data : []);
      if (dbRes && dbRes.data) {
        setDashboardData(dbRes.data);
        // Telefon detay görünümü için varsayılan seçimi ata
        if (dbRes.data.communication?.recent_calls?.length > 0) {
          setSelectedPhoneItem(dbRes.data.communication.recent_calls[0]);
          setSelectedPhoneType('call');
        } else if (dbRes.data.communication?.recent_messages?.length > 0) {
          setSelectedPhoneItem(dbRes.data.communication.recent_messages[0]);
          setSelectedPhoneType('msg');
        }
      }
      setDashboardLoading(false);
      axios.get('/api/admin/my-brand-access', { headers })
        .then(r => setBrandAccess(r.data))
        .catch(() => {});
      // AI overview yükle
      const aiRes = await axios.get('/api/admin/ai-overview', { headers });
      setAiOverview(Array.isArray(aiRes.data) ? aiRes.data : []);
    } catch (error) {
      console.error('Error:', error);
      if (error.response?.status === 401) {
        localStorage.removeItem('admin_token');
        setToken(null);
        navigate('/yonetim');
      }
    } finally {
      setLoading(false);
      setDashboardLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    setSubscriptionsLoading(true);
    try {
      const res = await axios.get('/api/admin/subscriptions', { headers: { Authorization: `Bearer ${token}` } });
      setSubscriptions(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error(error);
    }
    setSubscriptionsLoading(false);
  };

  const fetchRitsaAsistWaha = async () => {
    setRitsaAsistWahaLoading(true);
    try {
      const res = await axios.get('/api/admin/ritsa-asist-waha-settings', { headers: { Authorization: `Bearer ${token}` } });
      setRitsaAsistWaha(res.data);
      setRitsaAsistWahaForm({ waha_url: res.data.waha_url || '', waha_api_key: '', waha_session: res.data.waha_session || 'default' });
    } catch (error) {
      console.error(error);
    }
    setRitsaAsistWahaLoading(false);
    setRitsaAsistWahaLoaded(true);
  };


  const fetchReportsData = async (period = reportsPeriod) => {
    setReportsLoading(true);
    try {
      const res = await axios.get('/api/admin/reports', { params: { period }, headers: { Authorization: `Bearer ${token}` } });
      setReportsData(res.data);
    } catch (error) {
      console.error(error);
    }
    setReportsLoading(false);
  };

  const fetchAnnouncements = async () => {
    setAnnouncementsLoading(true);
    try {
      const res = await axios.get('/api/admin/announcements', { headers: { Authorization: `Bearer ${token}` } });
      setAnnouncements(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error(error);
    }
    setAnnouncementsLoading(false);
  };

  const openAnnouncementModal = (item = null) => {
    setAnnouncementForm(item ? { title: item.title, content: item.content, type: item.type } : { title: '', content: '', type: 'update' });
    setAnnouncementModal({ show: true, item });
  };

  const saveAnnouncement = async () => {
    if (!announcementForm.title.trim() || !announcementForm.content.trim()) {
      alert('Başlık ve içerik zorunludur.');
      return;
    }
    setAnnouncementSaving(true);
    try {
      if (announcementModal.item) {
        await axios.put(`/api/admin/announcements/${announcementModal.item.id}`, announcementForm, { headers: { Authorization: `Bearer ${token}` } });
      } else {
        await axios.post('/api/admin/announcements', announcementForm, { headers: { Authorization: `Bearer ${token}` } });
      }
      setAnnouncementModal({ show: false, item: null });
      fetchAnnouncements();
    } catch (error) {
      alert('Kaydedilemedi: ' + (error.response?.data?.error || error.message));
    }
    setAnnouncementSaving(false);
  };

  const toggleAnnouncementActive = async (item) => {
    try {
      await axios.put(`/api/admin/announcements/${item.id}`, { is_active: !item.is_active }, { headers: { Authorization: `Bearer ${token}` } });
      fetchAnnouncements();
    } catch (error) {
      alert('Güncellenemedi: ' + (error.response?.data?.error || error.message));
    }
  };

  const deleteAnnouncement = async (id) => {
    if (!window.confirm('Bu duyuru kalıcı olarak silinsin mi?')) return;
    try {
      await axios.delete(`/api/admin/announcements/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchAnnouncements();
    } catch (error) {
      alert('Silinemedi: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleReportsPeriodChange = (p) => {
    setReportsPeriod(p);
    fetchReportsData(p);
  };

  const handleSaveRitsaAsistWaha = async () => {
    setRitsaAsistWahaSaving(true);
    try {
      await axios.put('/api/admin/ritsa-asist-waha-settings', ritsaAsistWahaForm, { headers: { Authorization: `Bearer ${token}` } });
      await fetchRitsaAsistWaha();
      alert('Ritsa Asist WAHA ayarları kaydedildi.');
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.error || error.message));
    }
    setRitsaAsistWahaSaving(false);
  };

  const fetchSubscriptionPayments = async (branchId) => {
    try {
      const res = await axios.get(`/api/admin/subscriptions/${branchId}/payments`, { headers: { Authorization: `Bearer ${token}` } });
      setSubscriptionPayments(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error(error);
      setSubscriptionPayments([]);
    }
  };

  const handleSaveSubscription = async (branchId, form) => {
    try {
      await axios.post(`/api/admin/subscriptions/${branchId}`, form, { headers: { Authorization: `Bearer ${token}` } });
      setSubscriptionEditModal(null);
      fetchSubscriptions();
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleRecordPayment = async (branchId, form) => {
    try {
      await axios.post(`/api/admin/subscriptions/${branchId}/payments`, form, { headers: { Authorization: `Bearer ${token}` } });
      setSubscriptionPaymentModal(null);
      fetchSubscriptions();
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleToggleSubscriptionSuspend = async (branchId, suspend) => {
    if (!window.confirm(suspend ? 'Bu şubenin yazma erişimi durdurulsun mu?' : 'Bu şube tekrar aktif edilsin mi?')) return;
    try {
      await axios.post(`/api/admin/subscriptions/${branchId}/${suspend ? 'suspend' : 'reactivate'}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchSubscriptions();
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleApprove = async (id, type) => {
    try {
      const endpoint = type === 'registration' ? `/api/admin/approve-registration/${id}` : `/api/admin/approve-branch/${id}`;
      await axios.post(endpoint, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch (error) {
      alert('Hata: ' + error.response?.data?.error);
    }
  };

  const handleReject = async () => {
    const { id, type } = rejectModal;
    try {
      const endpoint = type === 'registration' ? `/api/admin/reject-registration/${id}` : `/api/admin/reject-branch/${id}`;
      await axios.post(endpoint, { reason: 'Reddedildi' }, { headers: { Authorization: `Bearer ${token}` } });
      setRejectModal({ show: false, id: null, type: '' });
      fetchData();
    } catch (error) {
      alert('Hata: ' + error.response?.data?.error);
    }
  };

  const handleToggleBusiness = async (id) => {
    try {
      const res = await axios.post(`/api/admin/toggle-business/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data?.warning) {
        alert('⚠️ ' + res.data.warning);
      }
      fetchData();
    } catch (error) {
      alert('Hata: ' + error.response?.data?.error);
    }
  };

  const handleToggleBranch = async (id) => {
    try {
      await axios.post(`/api/admin/toggle-branch/${id}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch (error) {
      alert('Hata: ' + error.response?.data?.error);
    }
  };

  const handleEditBranch = (id) => {
    const branch = approvedBranches.find(b => b.id === id);
    if (branch) {
      setEditForm({
        name: branch.name || '',
        city: branch.city || '',
        address: branch.address || '',
        industry: branch.business_industry || branch.industry || 'beauty_salon'
      });
      setEditBranchModal({ show: true, branch });
    }
  };

  const submitEditBranch = async () => {
    const { id } = editBranchModal.branch;
    const { name, city, address, industry } = editForm;
    if (!name.trim()) {
      alert('Şube adı boş olamaz');
      return;
    }
    try {
      await axios.put(`/api/admin/update-branch/${id}`, { name, city, address, industry }, { headers: { Authorization: `Bearer ${token}` } });
      setEditBranchModal({ show: false, branch: null });
      fetchData();
    } catch (error) {
      alert('Hata: ' + error.response?.data?.error);
    }
  };

  const handleDeleteBranch = async (id) => {
    if (!confirm('Şubeyi silmek istediğinizden emin misiniz?')) return;
    try {
      await axios.delete(`/api/admin/delete-branch/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch (error) {
      alert('Hata: ' + error.response?.data?.error);
    }
  };

  const handleSelectAIBranch = async (branch) => {
    setSelectedAIBranch(branch);
    setAiCenterTab('agents');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [agentsRes, callsRes, settingsRes, statsRes] = await Promise.all([
        axios.get(`/api/admin/ai-branch/${branch.branch_id}/agents`, { headers }),
        axios.get(`/api/admin/ai-branch/${branch.branch_id}/calls`, { headers }),
        axios.get(`/api/admin/ai-branch/${branch.branch_id}/settings`, { headers }),
        axios.get(`/api/admin/ai-branch/${branch.branch_id}/stats`, { headers }),
      ]);
      setAiBranchAgents(Array.isArray(agentsRes.data) ? agentsRes.data : []);
      setAiBranchCalls(Array.isArray(callsRes.data) ? callsRes.data : []);
      setAiBranchSettings(settingsRes.data || null);
      setAiBranchStats(statsRes.data || null);
      // Settings form doldurmak
      const s = settingsRes.data || {};
      setAiSettingsForm({
        provider: s.provider || 'openai',
        model: s.model || 'gpt-4o',
        gemini_model: s.gemini_model || 'gemini-2.0-flash',
        openai_api_key: s.openai_api_key || '',
        gemini_api_key: s.gemini_api_key || '',
        system_prompt: s.system_prompt || '',
        confidence_threshold: s.confidence_threshold || 85,
        max_history: s.max_history || 20,
        language: s.language || 'tr',
        is_enabled: s.is_enabled ? 1 : 0,
      });
    } catch (e) { console.error(e); }
  };

  const handleToggleAI = async (branch) => {
    try {
      const newEnabled = !branch.is_enabled;
      await axios.post(`/api/admin/ai-branch/${branch.branch_id}/toggle`,
        { enabled: newEnabled },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchData();
      if (selectedAIBranch?.branch_id === branch.branch_id) {
        setSelectedAIBranch({ ...selectedAIBranch, is_enabled: newEnabled });
      }
    } catch (e) { alert('Hata: ' + e.response?.data?.error); }
  };

  // Modal, sube icin YUKLENMIS ayarlarla acilir. Onceden system_prompt bos string
  // ile sifirlaniyor ve kaydedildiginde subenin promptunu siliyordu.
  const openAISettingsModal = (branch) => {
    const s = aiBranchSettings || {};
    setAiSettingsForm({
      provider: s.provider || branch.provider || 'openai',
      model: s.model || branch.model || 'gpt-4o',
      gemini_model: s.gemini_model || branch.gemini_model || 'gemini-2.0-flash',
      openai_api_key: '',
      gemini_api_key: '',
      system_prompt: s.system_prompt || '',
      confidence_threshold: s.confidence_threshold || 85,
      max_history: s.max_history || 20,
      language: s.language || 'tr',
    });
    setAiSettingsModal({ show: true, branch });
  };

  const submitAISettings = async () => {
    if (!selectedAIBranch) return;
    setAiSaving(true);
    const payload = {};
    Object.entries(aiSettingsForm).forEach(([k, v]) => { payload[k] = v; });
    try {
      await axios.put(`/api/admin/ai-branch/${selectedAIBranch.branch_id}/settings`, payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      alert('✅ Ayarlar kaydedildi');
      handleSelectAIBranch(selectedAIBranch);
    } catch (e) { alert('Hata: ' + e.response?.data?.error); }
    setAiSaving(false);
  };

  const openAgentModal = (agent = null) => {
    setAgentForm(agent ? {
      name: agent.name || '',
      type: agent.type || 'voice',
      language: agent.language || 'tr',
      language_label: agent.language_label || 'Türkçe',
      working_hours: agent.working_hours || '7/24',
      status: agent.status || 'active',
    } : { name: '', type: 'voice', language: 'tr', language_label: 'Türkçe', working_hours: '7/24', status: 'active' });
    setAgentModal({ show: true, agent });
  };

  const saveAgent = async () => {
    if (!agentForm.name.trim() || !selectedAIBranch) return;
    const bid = selectedAIBranch.branch_id;
    const headers = { Authorization: `Bearer ${token}` };
    try {
      if (agentModal.agent) {
        await axios.put(`/api/admin/ai-branch/${bid}/agents/${agentModal.agent.id}`, agentForm, { headers });
      } else {
        await axios.post(`/api/admin/ai-branch/${bid}/agents`, agentForm, { headers });
      }
      setAgentModal({ show: false, agent: null });
      handleSelectAIBranch(selectedAIBranch);
    } catch (e) { alert('Hata: ' + e.response?.data?.error); }
  };

  const deleteAgent = async (agentId) => {
    if (!confirm('Agent silinsin mi?') || !selectedAIBranch) return;
    try {
      await axios.delete(`/api/admin/ai-branch/${selectedAIBranch.branch_id}/agents/${agentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      handleSelectAIBranch(selectedAIBranch);
    } catch (e) { alert('Hata: ' + e.response?.data?.error); }
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/yonetim');
  };

  // Admin Kullanıcı Yönetimi İşlemleri
  const fetchAdminUsers = async () => {
    setAdminUsersLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const res = await axios.get('/api/admin/users', { headers });
      setAdminUsers(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      console.error('Admin users fetch error:', error);
    } finally {
      setAdminUsersLoading(false);
    }
  };

  // Başvurunun/firmanın markasını değiştirir; firma ana şubesi de aynı markaya geçer.
  const assignFirmBrand = async (registrationId, brand) => {
    try {
      await axios.put(`/api/admin/registrations/${registrationId}/brand`, { brand }, { headers: { Authorization: `Bearer ${token}` } });
      setApprovedBusinesses(prev => prev.map(f => f.id === registrationId ? { ...f, brand } : f));
      setRegistrations(prev => prev.map(r => r.id === registrationId ? { ...r, brand } : r));
    } catch (e) {
      alert(e.response?.data?.error || 'Marka atanamadı');
    }
  };

  const [brandList, setBrandList] = useState([]);
  const fetchBrandList = async () => {
    try {
      const res = await axios.get('/api/admin/brand-list', { headers: { Authorization: `Bearer ${token}` } });
      setBrandList(res.data || []);
    } catch (e) { /* marka listesi gelmezse bölüm gizli kalır */ }
  };

  const openAdminUserModal = (user = null) => {
    if (!brandList.length) fetchBrandList();
    setAdminUserForm(user ? {
      username: user.username || '',
      password: '',
      email: user.email || '',
      role: user.role || 'admin',
      permissions: Array.isArray(user.permissions) ? user.permissions : []
    } : { username: '', password: '', email: '', role: 'admin', permissions: [] });
    setAdminUserModal({ show: true, user });
  };

  const saveAdminUser = async () => {
    if (!adminUserForm.username.trim()) {
      alert('Kullanıcı adı zorunludur');
      return;
    }
    if (!adminUserModal.user && !adminUserForm.password.trim()) {
      alert('Yeni kullanıcı için şifre zorunludur');
      return;
    }
    const headers = { Authorization: `Bearer ${token}` };
    try {
      if (adminUserModal.user) {
        await axios.put(`/api/admin/users/${adminUserModal.user.id}`, adminUserForm, { headers });
      } else {
        await axios.post('/api/admin/users', adminUserForm, { headers });
      }
      setAdminUserModal({ show: false, user: null });
      fetchAdminUsers();
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.error || error.message));
    }
  };

  const deleteAdminUser = async (userId) => {
    if (!confirm('Bu admin kullanıcısını silmek istediğinize emin misiniz?')) return;
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.delete(`/api/admin/users/${userId}`, { headers });
      fetchAdminUsers();
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.error || error.message));
    }
  };

  useEffect(() => {
    if (activeTab === 'admins' && token) {
      fetchAdminUsers();
    }
  }, [activeTab, token]);

  const handleAddDemoData = async () => {
    if (!confirm('Demo verileri eklemek istediğinizden emin misiniz?')) return;
    try {
      const response = await axios.post('/api/admin/generate-demo-data', {}, { headers: { Authorization: `Bearer ${token}` } });
      alert(response.data.message || 'Demo veriler başarıyla eklendi!');
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteIndustry = async (id) => {
    try {
      await axios.delete(`/api/admin/industries/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch (error) {
      alert('Hata: ' + error.response?.data?.error);
    }
  };

  const handleSaveIndustry = async () => {
    try {
      await axios.post('/api/admin/industries', {
        industry: industryForm.industry,
        display_name: industryForm.display_name,
        description: industryForm.description,
        modules: industryForm.modules
      }, { headers: { Authorization: `Bearer ${token}` } });
      setNewIndustryModal(false);
      fetchData();
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleToggleIndustry = async (industry, enabled) => {
    try {
      await axios.put(`/api/admin/industry-settings/${industry}`, { enabled }, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch (error) {
      alert('Hata: ' + error.response?.data?.error);
    }
  };

  const handleUpdateModules = async (industry, modules) => {
    try {
      await axios.put(`/api/admin/industry-modules/${industry}`, { modules }, { headers: { Authorization: `Bearer ${token}` } });
      fetchData();
    } catch (error) {
      alert('Hata: ' + error.response?.data?.error);
    }
  };

  const fetchIndustryAutomations = async (industry) => {
    setLoadingAutomations(true);
    try {
      const res = await axios.get(`/api/admin/industries/${industry}/automations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIndustryAutomations(prev => ({ ...prev, [industry]: res.data }));
    } catch (error) {
      console.error(error);
    } finally {
      setLoadingAutomations(false);
    }
  };

  // ── Sektör AI prompt standardı ────────────────────────────────────────────
  const fetchIndustryPrompt = async (industry, channel = industryPromptChannel) => {
    try {
      const qs = channel ? `?channel=${channel}` : '';
      const [detail, branches] = await Promise.all([
        axios.get(`/api/admin/industry-prompts/${industry}${qs}`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`/api/admin/industry-prompts/${industry}/branches`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setIndustryPromptData(prev => ({ ...prev, [industry]: detail.data }));
      setIndustryPromptText(detail.data.system_prompt || '');
      setIndustryPromptDirty(false);
      setIndustryBranches(branches.data || []);
      setPreviewBranchId('');
      setPromptPreview(null);
      setShowPromptHistory(false);
    } catch (error) {
      console.error(error);
    }
  };

  const selectIndustryPromptChannel = (industry, channel) => {
    if (industryPromptDirty && !window.confirm('Kaydedilmemiş prompt değişikliğiniz var. Yine de kanal değiştirmek istiyor musunuz?')) {
      return;
    }
    setIndustryPromptChannel(channel);
    fetchIndustryPrompt(industry, channel);
  };

  const handleSaveIndustryPrompt = async (industry) => {
    if (!industryPromptText.trim()) {
      alert('Standart prompt boş olamaz — bu sektördeki tüm katmanlı şubeleri etkiler.');
      return;
    }
    setIndustryPromptSaving(true);
    try {
      const res = await axios.put(`/api/admin/industry-prompts/${industry}`,
        { system_prompt: industryPromptText, channel: industryPromptChannel || undefined },
        { headers: { Authorization: `Bearer ${token}` } });
      alert(`✅ Kaydedildi (sürüm v${res.data.version}). Bu sektördeki katmanlı şubelere anında yansıdı.`);
      setIndustryPromptDirty(false);
      await fetchIndustryPrompt(industry);
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.error || error.message));
    } finally {
      setIndustryPromptSaving(false);
    }
  };

  const fetchIndustryPromptHistory = async (industry) => {
    try {
      const qs = industryPromptChannel ? `?channel=${industryPromptChannel}` : '';
      const res = await axios.get(`/api/admin/industry-prompts/${industry}/history${qs}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIndustryPromptHistory(res.data || []);
      setShowPromptHistory(true);
    } catch (error) {
      console.error(error);
    }
  };

  const handleRestoreIndustryPrompt = async (industry, historyId) => {
    if (!window.confirm('Bu sürüme geri dönülsün mü? Mevcut metin geçmişe kaydedilecek.')) return;
    try {
      await axios.post(`/api/admin/industry-prompts/${industry}/history/${historyId}/restore`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchIndustryPrompt(industry);
      alert('✅ Eski sürüme dönüldü.');
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.error || error.message));
    }
  };

  const handlePreviewComposed = async (industry, branchId) => {
    if (!branchId) { setPromptPreview(null); return; }
    try {
      const res = await axios.get(`/api/admin/industry-prompts/${industry}/preview?branch_id=${branchId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPromptPreview(res.data);
    } catch (error) {
      setPromptPreview(null);
      alert('Önizleme alınamadı: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleOpenAutomationTemplateModal = (industry, template = null) => {
    setAutomationTemplateForm(template ? {
      name: template.name || '',
      trigger_type: template.trigger_type || 'appointment_before',
      trigger_value: template.trigger_value !== null ? template.trigger_value : 24,
      channel: template.channel || 'whatsapp',
      template: template.template || ''
    } : { name: '', trigger_type: 'appointment_before', trigger_value: 24, channel: 'whatsapp', template: '' });
    setAutomationTemplateModal({ show: true, template, industry });
  };

  const handleSaveAutomationTemplate = async () => {
    const { template, industry } = automationTemplateModal;
    const headers = { Authorization: `Bearer ${token}` };
    try {
      if (template) {
        await axios.put(`/api/admin/industries/${industry}/automations/${template.id}`, automationTemplateForm, { headers });
      } else {
        await axios.post(`/api/admin/industries/${industry}/automations`, automationTemplateForm, { headers });
      }
      setAutomationTemplateModal({ show: false, template: null, industry: '' });
      fetchIndustryAutomations(industry);
    } catch (error) {
      alert('Hata: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteAutomationTemplate = async (industry, id) => {
    if (!confirm('Şablonu silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`/api/admin/industries/${industry}/automations/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchIndustryAutomations(industry);
    } catch (error) {
      alert('Hata: ' + error.response?.data?.error);
    }
  };

  if (!token) return <Navigate to="/yonetim" />;
  if (loading) return <div className="admin-loading">Yükleniyor...</div>;

  // Sekme değişince eski ayrı onClick'lerdeki yan etkiler (veri çekme/state
  // sıfırlama) burada tek yerden korunuyor - alttaki ikonlu menü artık bu
  // listeden üretiliyor, her sekme için ayrı JSX bloğu yazmıyoruz.
  const handleNavClick = (key) => {
    setActiveTab(key);
    if (key === 'subscriptions') fetchSubscriptions();
    if (key === 'custom_companies') setSelectedCustomCompanyId(null);
    if (key === 'ai-center' && !ritsaAsistWahaLoaded) fetchRitsaAsistWaha();
    if (key === 'reports' && !reportsData) fetchReportsData();
    if (key === 'announcements') fetchAnnouncements();
    if (key === 'api') setApiTab('keys');
    setMoreNavOpen(false);
  };

  const allNavItems = [
    { key: 'dashboard', icon: '📊', label: 'Genel Bakış' },
    { key: 'registrations', icon: '📝', label: 'Kayıt Başvuruları', count: registrations.length },
    { key: 'branches', icon: '🏪', label: 'Şube Talepleri', count: branchRequests.length },
    { key: 'firms', icon: '📋', label: 'CRM Firmaları', count: approvedBusinesses.length },
    { key: 'subscriptions', icon: '💳', label: 'Abonelikler' },
    { key: 'custom_companies', icon: '🏢', label: 'Firma Yönetimi' },
    { key: 'call_center', icon: '📞', label: 'Çağrı Merkezi' },
    { key: 'industries', icon: '🏷️', label: 'Sektör Ayarları' },
    { key: 'ai-center', icon: '🤖', label: 'AI Center', count: aiOverview.length },
    { key: 'reports', icon: '📈', label: 'Raporlar' },
    { key: 'announcements', icon: '📢', label: 'Duyurular' },
    { key: 'brands', icon: '🎨', label: 'Marka Ayarları' },
    { key: 'api', icon: '🔌', label: 'API Yönetimi' },
    { key: 'admins', icon: '👤', label: 'Adminler' },
  ].filter(item => isPageEnabled(item.key));

  const primaryNavItems = allNavItems.slice(0, 5);
  const moreNavItems = allNavItems.slice(5);

  return (
    <div className="admin-panel">
      <header className="admin-header">
        <h1>Admin Panel</h1>
        <div style={{ display: 'flex', gap: '12px' }}>
          <button onClick={handleLogout} className="logout-btn">Çıkış</button>
        </div>
      </header>

      {/* Alt ikonlu gezinme çubuğu - tenant CRM'in mobil alt navigasyonuyla aynı
          desen, hem mobilde hem masaüstünde sabit şekilde altta duruyor. */}
      <nav className="admin-bottom-nav">
        {primaryNavItems.map(item => (
          <button
            key={item.key}
            className={`admin-nav-tab${activeTab === item.key ? ' admin-nav-tab-active' : ''}`}
            onClick={() => handleNavClick(item.key)}
          >
            <span className="admin-nav-icon-wrap">
              <span className="admin-nav-icon">{item.icon}</span>
              {!!item.count && <span className="admin-nav-badge">{item.count > 99 ? '99+' : item.count}</span>}
            </span>
            <span className="admin-nav-label">{item.label}</span>
          </button>
        ))}
        {moreNavItems.length > 0 && (
          <button
            className={`admin-nav-tab${moreNavItems.some(i => i.key === activeTab) ? ' admin-nav-tab-active' : ''}`}
            onClick={() => setMoreNavOpen(!moreNavOpen)}
          >
            <span className="admin-nav-icon-wrap"><span className="admin-nav-icon">☰</span></span>
            <span className="admin-nav-label">Menü</span>
          </button>
        )}
      </nav>

      {moreNavOpen && (
        <div className="admin-more-overlay" onClick={() => setMoreNavOpen(false)}>
          <div className="admin-more-drawer" onClick={e => e.stopPropagation()}>
            <div className="admin-more-grid">
              {moreNavItems.map(item => (
                <button
                  key={item.key}
                  className={`admin-more-item${activeTab === item.key ? ' admin-more-item-active' : ''}`}
                  onClick={() => handleNavClick(item.key)}
                >
                  <span className="admin-nav-icon-wrap">
                    <span className="admin-nav-icon">{item.icon}</span>
                    {!!item.count && <span className="admin-nav-badge">{item.count > 99 ? '99+' : item.count}</span>}
                  </span>
                  <span>{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="admin-content">
        {activeTab === 'dashboard' && isPageEnabled('dashboard') && (
          <div>
            {dashboardLoading || !dashboardData ? (
              <div className="admin-loading" style={{ minHeight: '300px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div className="loading-spinner"></div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {/* KPI Overview Grid */}
                <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                  <div className="stat-card" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '20px' }}>
                    <div className="stat-icon stat-icon-primary">🏢</div>
                    <div className="stat-content">
                      <div className="stat-label">Toplam Firma</div>
                      <div className="stat-value">{dashboardData.stats.total_firms}</div>
                      <div className="stat-change stat-change-positive">{dashboardData.stats.total_branches} Aktif Şube</div>
                    </div>
                  </div>

                  <div className="stat-card" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '20px' }}>
                    <div className="stat-icon" style={{ background: 'rgba(var(--secondary-rgb), 0.15)', color: 'var(--secondary)' }}>⏳</div>
                    <div className="stat-content">
                      <div className="stat-label">Bekleyen Onaylar</div>
                      <div className="stat-value">{dashboardData.stats.pending_registrations + dashboardData.stats.pending_branch_requests}</div>
                      <div className="stat-change stat-change-negative" style={{ color: 'var(--danger)' }}>
                        {dashboardData.stats.pending_registrations} Kayıt / {dashboardData.stats.pending_branch_requests} Şube
                      </div>
                    </div>
                  </div>

                  <div className="stat-card" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '20px' }}>
                    <div className="stat-icon" style={{ background: 'rgba(16, 185, 129, 0.12)', color: '#10b981' }}>💰</div>
                    <div className="stat-content">
                      <div className="stat-label">Tahmini Aylık MRR</div>
                      <div className="stat-value">₺{(dashboardData.stats.estimated_mrr).toLocaleString('tr-TR')}</div>
                      <div className="stat-change stat-change-positive">SaaS Abonelik Geliri</div>
                    </div>
                  </div>

                  <div className="stat-card" style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '20px' }}>
                    <div className="stat-icon stat-icon-warning">📈</div>
                    <div className="stat-content">
                      <div className="stat-label">Platform Hacmi</div>
                      <div className="stat-value">₺{Math.round(dashboardData.stats.total_revenue).toLocaleString('tr-TR')}</div>
                      <div className="stat-change stat-change-positive">
                        {dashboardData.stats.total_appointments} Randevu · {dashboardData.stats.total_customers} Müşteri
                      </div>
                    </div>
                  </div>
                </div>

                {/* CSS Animasyon ve Scrollbar Stilleri */}
                <style>{`
                  @keyframes pulse {
                    0% { transform: scale(0.95); opacity: 0.5; box-shadow: 0 0 0 0 rgba(var(--primary-rgb), 0.4); }
                    70% { transform: scale(1); opacity: 1; box-shadow: 0 0 0 10px rgba(var(--primary-rgb), 0); }
                    100% { transform: scale(0.95); opacity: 0.5; box-shadow: 0 0 0 0 rgba(var(--primary-rgb), 0); }
                  }
                  .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                  }
                  .custom-scrollbar::-webkit-scrollbar-track {
                    background: rgba(255,255,255,0.01);
                  }
                  .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: rgba(255,255,255,0.08);
                    border-radius: 3px;
                  }
                  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: rgba(255,255,255,0.15);
                  }
                `}</style>

                {/* AI İletişim Hub ve Telefon Simülatörü */}
                <div className="card" style={{ padding: '28px', borderRadius: '24px', background: 'linear-gradient(145deg, rgba(255,255,255,0.015), rgba(255,255,255,0.002))', border: '1px solid var(--border)', position: 'relative', overflow: 'hidden' }}>
                  {/* Arka plan parlama efektleri */}
                  <div style={{ position: 'absolute', top: '-10%', right: '-10%', width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(var(--primary-rgb), 0.08) 0%, transparent 70%)', filter: 'blur(50px)', pointerEvents: 'none' }}></div>
                  <div style={{ position: 'absolute', bottom: '-10%', left: '-10%', width: '300px', height: '300px', background: 'radial-gradient(circle, rgba(var(--secondary-rgb), 0.06) 0%, transparent 70%)', filter: 'blur(50px)', pointerEvents: 'none' }}></div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', position: 'relative', zIndex: 1 }}>
                    <div>
                      <h2 style={{ fontSize: '18px', fontWeight: 800, margin: 0, background: 'linear-gradient(135deg, #fff, #a5b4fc)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', display: 'flex', alignItems: 'center', gap: '8px' }}>
                        🤖 AI İletişim Hub & Telefon Simülatörü
                      </h2>
                      <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--text-muted)' }}>
                        AI asistanlarının gerçekleştirdiği aramaları ve gelen mesajları detaylı olarak izleyin (Açılan telefon simülasyonu ile)
                      </p>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '12px' }}>
                      <div style={{ background: 'rgba(var(--primary-rgb), 0.08)', border: '1px solid rgba(var(--primary-rgb), 0.15)', borderRadius: '12px', padding: '6px 14px', fontSize: '11px', color: 'var(--primary)' }}>
                        📞 Arama Cevaplama: <strong>%{dashboardData.communication?.calls?.success_rate || 89}%</strong>
                      </div>
                      <div style={{ background: 'rgba(var(--secondary-rgb), 0.08)', border: '1px solid rgba(var(--secondary-rgb), 0.15)', borderRadius: '12px', padding: '6px 14px', fontSize: '11px', color: '#a78bfa' }}>
                        💬 Mesaj Yanıtlama: <strong>%{dashboardData.communication?.messages?.response_rate || 93}%</strong>
                      </div>
                    </div>
                  </div>

                  <div className="dashboard-two-col-grid" style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '28px', position: 'relative', zIndex: 1 }}>
                    {/* Sol Panel: Aramalar & Mesajlar Listesi */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                      <div style={{ display: 'flex', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: '12px', padding: '4px' }}>
                        <button 
                          onClick={() => {
                            setSelectedPhoneType('call');
                            if (dashboardData.communication?.recent_calls?.length > 0) {
                              setSelectedPhoneItem(dashboardData.communication.recent_calls[0]);
                            }
                          }}
                          style={{
                            flex: 1,
                            padding: '8px 16px',
                            background: selectedPhoneType === 'call' ? 'rgba(255,255,255,0.08)' : 'transparent',
                            border: 0,
                            borderRadius: '8px',
                            color: selectedPhoneType === 'call' ? '#fff' : 'var(--text-muted)',
                            fontSize: '13px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                        >
                          📞 AI Telefon Aramaları ({dashboardData.communication?.calls?.total || 0})
                        </button>
                        <button 
                          onClick={() => {
                            setSelectedPhoneType('msg');
                            if (dashboardData.communication?.recent_messages?.length > 0) {
                              setSelectedPhoneItem(dashboardData.communication.recent_messages[0]);
                            }
                          }}
                          style={{
                            flex: 1,
                            padding: '8px 16px',
                            background: selectedPhoneType === 'msg' ? 'rgba(255,255,255,0.08)' : 'transparent',
                            border: 0,
                            borderRadius: '8px',
                            color: selectedPhoneType === 'msg' ? '#fff' : 'var(--text-muted)',
                            fontSize: '13px',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                          }}
                        >
                          💬 Sosyal Medya & WhatsApp ({dashboardData.communication?.messages?.total || 0})
                        </button>
                      </div>

                      {/* Liste İçeriği */}
                      <div className="custom-scrollbar" style={{ maxHeight: '420px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', paddingRight: '4px' }}>
                        {selectedPhoneType === 'call' ? (
                          dashboardData.communication?.recent_calls?.map((call, idx) => {
                            const isSelected = selectedPhoneItem?.id === call.id;
                            const isSuccess = call.status === 'completed';
                            return (
                              <div 
                                key={call.id || idx}
                                onClick={() => setSelectedPhoneItem(call)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: '12px 16px',
                                  background: isSelected ? 'rgba(0, 242, 254, 0.05)' : 'rgba(255,255,255,0.01)',
                                  border: isSelected ? '1px solid rgba(0, 242, 254, 0.3)' : '1px solid var(--border)',
                                  borderRadius: '16px',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  boxShadow: isSelected ? '0 0 15px rgba(0, 242, 254, 0.05)' : 'none'
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                  <div style={{
                                    width: '38px',
                                    height: '38px',
                                    borderRadius: '50%',
                                    background: isSuccess ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)',
                                    color: isSuccess ? '#10b981' : '#ef4444',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    position: 'relative'
                                  }}>
                                    {isSuccess ? '✓' : '✗'}
                                    {isSuccess && <span style={{ position: 'absolute', bottom: 0, right: 0, width: '10px', height: '10px', borderRadius: '50%', background: '#10b981', border: '2px solid var(--bg-card)', animation: 'pulse 1.5s infinite' }}></span>}
                                  </div>
                                  <div>
                                    <div style={{ color: '#fff', fontWeight: 650, fontSize: '13px' }}>{call.caller_name || 'Gizli Numara'}</div>
                                    <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '2px' }}>
                                      {call.caller_phone} · <span style={{ color: 'rgba(255,255,255,0.4)' }}>{call.branch_name || 'Şube Tanımsız'}</span>
                                    </div>
                                  </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                  <div style={{ textAlign: 'right' }}>
                                    <span style={{ 
                                      fontSize: '9px', 
                                      fontWeight: 800, 
                                      padding: '2px 6px', 
                                      borderRadius: '6px', 
                                      background: call.sentiment === 'positive' ? 'rgba(16, 185, 129, 0.12)' : (call.sentiment === 'negative' ? 'rgba(239, 68, 68, 0.12)' : 'rgba(255,255,255,0.06)'), 
                                      color: call.sentiment === 'positive' ? '#10b981' : (call.sentiment === 'negative' ? '#ef4444' : 'rgba(255,255,255,0.6)'),
                                      textTransform: 'uppercase'
                                    }}>
                                      {call.sentiment === 'positive' ? 'Pozitif 😊' : (call.sentiment === 'negative' ? 'Şikayet 😠' : 'Nötr 😐')}
                                    </span>
                                    <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '4px' }}>
                                      {call.duration_seconds > 0 ? `${Math.floor(call.duration_seconds / 60)}dk ${call.duration_seconds % 60}sn` : 'Cevapsız'}
                                    </div>
                                  </div>
                                  <div style={{ color: 'rgba(255,255,255,0.2)', fontSize: '14px' }}>➔</div>
                                </div>
                              </div>
                            );
                          })
                        ) : (
                          dashboardData.communication?.recent_messages?.map((msg, idx) => {
                            const isSelected = selectedPhoneItem?.id === msg.id;
                            const platformIcons = {
                              whatsapp: '🟢 WA',
                              instagram: '📸 IG',
                              messenger: '🔵 FB',
                              tiktok: '🎵 TT',
                              sms: '✉️ SMS'
                            };
                            return (
                              <div 
                                key={msg.id || idx}
                                onClick={() => setSelectedPhoneItem(msg)}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'space-between',
                                  padding: '12px 16px',
                                  background: isSelected ? 'rgba(139, 92, 246, 0.05)' : 'rgba(255,255,255,0.01)',
                                  border: isSelected ? '1px solid rgba(139, 92, 246, 0.3)' : '1px solid var(--border)',
                                  borderRadius: '16px',
                                  cursor: 'pointer',
                                  transition: 'all 0.2s',
                                  boxShadow: isSelected ? '0 0 15px rgba(139, 92, 246, 0.05)' : 'none'
                                }}
                              >
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                  <div style={{
                                    width: '38px',
                                    height: '38px',
                                    borderRadius: '50%',
                                    background: 'rgba(255, 255, 255, 0.03)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    border: '1px solid var(--border)'
                                  }}>
                                    {msg.platform === 'whatsapp' ? '💬' : msg.platform === 'instagram' ? '📸' : '✉️'}
                                  </div>
                                  <div>
                                    <div style={{ color: '#fff', fontWeight: 650, fontSize: '13px' }}>{msg.customer_name}</div>
                                    <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '2px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                      <span style={{ 
                                        color: msg.platform === 'whatsapp' ? '#10b981' : msg.platform === 'instagram' ? '#ec4899' : '#3b82f6',
                                        fontWeight: 700,
                                        fontSize: '9px',
                                        textTransform: 'uppercase'
                                      }}>{platformIcons[msg.platform] || msg.platform}</span>
                                      · <span>{msg.branch_name || 'Şube Tanımsız'}</span>
                                      {msg.account_label && <>· <span style={{ color: '#60a5fa' }}>{msg.account_label}</span></>}
                                    </div>
                                  </div>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                  <div style={{ textAlign: 'right' }}>
                                    <span style={{ 
                                      fontSize: '9px', 
                                      fontWeight: 800, 
                                      padding: '2px 6px', 
                                      borderRadius: '6px', 
                                      background: msg.is_read ? 'rgba(16, 185, 129, 0.12)' : 'rgba(251, 191, 36, 0.12)', 
                                      color: msg.is_read ? '#10b981' : '#fbbf24'
                                    }}>
                                      {msg.is_read ? 'Yanıtlandı ✓' : 'Bekliyor ⏳'}
                                    </span>
                                    <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '4px' }}>
                                      {new Date(msg.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                  </div>
                                  <div style={{ color: 'rgba(255,255,255,0.2)', fontSize: '14px' }}>➔</div>
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>

                    {/* Sağ Panel: Telefon Simülatörü */}
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
                      <div style={{
                        width: '280px',
                        height: '460px',
                        borderRadius: '36px',
                        border: '8px solid #1f2937',
                        background: '#090a0f',
                        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 30px rgba(0, 242, 254, 0.1)',
                        position: 'relative',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                      }}>
                        {/* Hoparlör Ahizesi */}
                        <div style={{
                          position: 'absolute',
                          top: 0,
                          left: '50%',
                          transform: 'translateX(-50%)',
                          width: '100px',
                          height: '18px',
                          background: '#1f2937',
                          borderBottomLeftRadius: '12px',
                          borderBottomRightRadius: '12px',
                          zIndex: 10,
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center'
                        }}>
                          <div style={{ width: '40px', height: '3px', background: '#374151', borderRadius: '2px', marginBottom: '4px' }}></div>
                        </div>

                        {/* Telefon Ekran İçeriği */}
                        <div style={{ flex: 1, padding: '24px 14px 14px', display: 'flex', flexDirection: 'column', height: '100%', position: 'relative', zIndex: 5 }}>
                          
                          {/* Durum Çubuğu */}
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '9px', color: 'rgba(255,255,255,0.6)', marginBottom: '16px', padding: '0 4px' }}>
                            <span>09:41</span>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <span>📶</span>
                              <span>🔋 88%</span>
                            </div>
                          </div>

                          {selectedPhoneItem ? (
                            selectedPhoneType === 'call' ? (
                              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between', textAlign: 'center' }}>
                                <div style={{ marginTop: '10px' }}>
                                  {/* Konuşma Animasyonlu Avatar */}
                                  <div style={{ display: 'inline-flex', position: 'relative', justifyContent: 'center', alignItems: 'center', margin: '0 auto 12px' }}>
                                    <div style={{
                                      position: 'absolute',
                                      width: '66px',
                                      height: '66px',
                                      borderRadius: '50%',
                                      border: '2px solid rgba(0, 242, 254, 0.3)',
                                      animation: 'pulse 2s infinite'
                                    }}></div>
                                    <div style={{
                                      width: '54px',
                                      height: '54px',
                                      borderRadius: '50%',
                                      background: 'linear-gradient(135deg, #00f2fe, #4facfe)',
                                      color: '#fff',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      fontSize: '20px',
                                      fontWeight: 'bold',
                                      boxShadow: '0 0 15px rgba(0, 242, 254, 0.3)'
                                    }}>
                                      {(selectedPhoneItem.caller_name || 'G').charAt(0)}
                                    </div>
                                  </div>
                                  
                                  <h4 style={{ margin: '0 0 4px', color: '#fff', fontSize: '14px', fontWeight: 700 }}>{selectedPhoneItem.caller_name || 'Gizli Numara'}</h4>
                                  <div style={{ fontSize: '10px', color: 'var(--text-muted)' }}>{selectedPhoneItem.caller_phone}</div>
                                  <div style={{ 
                                    display: 'inline-block', 
                                    marginTop: '8px', 
                                    padding: '3px 8px', 
                                    borderRadius: '8px', 
                                    background: selectedPhoneItem.status === 'completed' ? 'rgba(16, 185, 129, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                                    color: selectedPhoneItem.status === 'completed' ? '#10b981' : '#ef4444',
                                    fontSize: '9px',
                                    fontWeight: 700
                                  }}>
                                    {selectedPhoneItem.status === 'completed' ? '🟢 AI Asistan Cevapladı' : '🔴 Cevapsız Arama'}
                                  </div>
                                </div>

                                {/* Arama Özeti Gösterimi */}
                                <div style={{ 
                                  flex: 1, 
                                  margin: '12px 0', 
                                  padding: '10px', 
                                  background: 'rgba(255,255,255,0.03)', 
                                  border: '1px solid rgba(255,255,255,0.06)', 
                                  borderRadius: '16px',
                                  textAlign: 'left',
                                  overflowY: 'auto',
                                  maxHeight: '150px'
                                }} className="custom-scrollbar">
                                  <div style={{ fontSize: '9px', fontWeight: 800, color: 'rgba(var(--primary-rgb), 0.8)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>Konuşma Özeti (Yapay Zeka)</div>
                                  <div style={{ fontSize: '10px', color: 'rgba(255,255,255,0.85)', lineHeight: '1.4' }}>
                                    {selectedPhoneItem.ai_summary || 'Görüşme analizi yapılıyor...'}
                                  </div>
                                  {selectedPhoneItem.sentiment && (
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '10px', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.05)' }}>
                                      <span style={{ fontSize: '9px', color: 'var(--text-muted)' }}>Analiz:</span>
                                      <span style={{ 
                                        fontSize: '9px', 
                                        fontWeight: 800, 
                                        padding: '1px 5px', 
                                        borderRadius: '4px',
                                        background: selectedPhoneItem.sentiment === 'positive' ? 'rgba(16, 185, 129, 0.15)' : (selectedPhoneItem.sentiment === 'negative' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(255,255,255,0.08)'),
                                        color: selectedPhoneItem.sentiment === 'positive' ? '#10b981' : (selectedPhoneItem.sentiment === 'negative' ? '#ef4444' : '#fff')
                                      }}>
                                        {selectedPhoneItem.sentiment === 'positive' ? 'Pozitif 😊' : (selectedPhoneItem.sentiment === 'negative' ? 'Şikayet 😠' : 'Nötr 😐')}
                                      </span>
                                    </div>
                                  )}
                                </div>

                                {/* Telefon Kontrol Butonları */}
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', marginBottom: '4px' }}>
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                    <button style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', border: 0, color: '#fff', fontSize: '12px', cursor: 'pointer' }}>🎙️</button>
                                    <span style={{ fontSize: '8px', color: 'var(--text-muted)' }}>Sessiz</span>
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                    <button style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', border: 0, color: '#fff', fontSize: '12px', cursor: 'pointer' }}>📱</button>
                                    <span style={{ fontSize: '8px', color: 'var(--text-muted)' }}>Klavye</span>
                                  </div>
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                                    <button style={{ width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,0.06)', border: 0, color: '#fff', fontSize: '12px', cursor: 'pointer' }}>🔊</button>
                                    <span style={{ fontSize: '8px', color: 'var(--text-muted)' }}>Hoparlör</span>
                                  </div>
                                </div>
                              </div>
                            ) : (
                              // CHAT EKRANI
                              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingBottom: '8px', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                                  <div style={{ width: '26px', height: '26px', borderRadius: '50%', background: 'rgba(var(--secondary-rgb), 0.2)', color: '#a78bfa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '11px', fontWeight: 'bold' }}>
                                    {selectedPhoneItem.customer_name.charAt(0)}
                                  </div>
                                  <div>
                                    <div style={{ color: '#fff', fontWeight: 700, fontSize: '11px' }}>{selectedPhoneItem.customer_name}</div>
                                    <div style={{ fontSize: '8px', color: '#10b981' }}>● Yapay Zeka Cevapladı</div>
                                  </div>
                                </div>

                                <div style={{ flex: 1, overflowY: 'auto', padding: '10px 0', display: 'flex', flexDirection: 'column', gap: '8px' }} className="custom-scrollbar">
                                  {/* Müşteri Gelen Mesajı */}
                                  <div style={{ display: 'flex', flexDirection: 'column', alignSelf: 'flex-start', maxWidth: '90%' }}>
                                    <div style={{ padding: '6px 10px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.04)', borderRadius: '12px 12px 12px 2px', color: 'rgba(255,255,255,0.95)', fontSize: '10px', lineHeight: '1.4' }}>
                                      {selectedPhoneItem.content}
                                    </div>
                                    <span style={{ fontSize: '7px', color: 'var(--text-muted)', marginTop: '2px', marginLeft: '4px' }}>Müşteri · {selectedPhoneItem.platform.toUpperCase()}</span>
                                  </div>

                                  {/* AI Tarafından Verilen Cevap */}
                                  <div style={{ display: 'flex', flexDirection: 'column', alignSelf: 'flex-end', maxWidth: '90%' }}>
                                    <div style={{ padding: '6px 10px', background: 'linear-gradient(135deg, rgba(var(--primary-rgb), 0.15) 0%, rgba(79, 172, 254, 0.15) 100%)', border: '1px solid rgba(var(--primary-rgb), 0.2)', borderRadius: '12px 12px 2px 12px', color: '#fff', fontSize: '10px', lineHeight: '1.4' }}>
                                      Değerli müşterimiz, talebiniz AI asistanımız tarafından analiz edilmiş ve şubemize başarıyla iletilmiştir. Sistemimize kayıtlı randevunuz onaylanmıştır.
                                    </div>
                                    <span style={{ fontSize: '7px', color: 'var(--primary)', marginTop: '2px', marginRight: '4px', textAlign: 'right' }}>AI Asistanı ✓</span>
                                  </div>
                                </div>

                                {/* Klavye Mock */}
                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                                  <input 
                                    type="text" 
                                    disabled 
                                    placeholder="AI otomatik yanıtladı..." 
                                    style={{ flex: 1, background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '99px', padding: '6px 10px', color: 'rgba(255,255,255,0.4)', fontSize: '9px' }} 
                                  />
                                  <button style={{ width: '22px', height: '22px', borderRadius: '50%', background: 'rgba(var(--primary-rgb), 0.2)', border: 0, color: 'var(--primary)', fontSize: '9px', cursor: 'not-allowed' }}>▶</button>
                                </div>
                              </div>
                            )
                          ) : (
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: '10px' }}>
                              Lütfen listeden bir kayıt seçin.
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* İletişim Trafiği Grafikleri */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '24px' }}>
                  {/* Arama Trafiği Trendi */}
                  <div className="card" style={{ padding: '24px', borderRadius: '24px' }}>
                    <h3 style={{ margin: '0 0 16px', fontSize: '15px', color: '#fff', fontWeight: 700 }}>📞 AI Arama Trafiği (Son 7 Gün)</h3>
                    <div style={{ height: '220px' }}>
                      <Line 
                        data={{
                          labels: dashboardData.communication?.calls?.trend?.map(t => t.date) || [],
                          datasets: [
                            {
                              label: 'Toplam Arama',
                              data: dashboardData.communication?.calls?.trend?.map(t => t.total) || [],
                              borderColor: 'rgba(255, 255, 255, 0.45)',
                              backgroundColor: 'transparent',
                              borderWidth: 2,
                              tension: 0.3,
                              pointRadius: 3
                            },
                            {
                              label: 'Cevaplanan Arama',
                              data: dashboardData.communication?.calls?.trend?.map(t => t.answered) || [],
                              borderColor: '#00f2fe',
                              backgroundColor: 'rgba(0, 242, 254, 0.05)',
                              borderWidth: 3,
                              tension: 0.3,
                              fill: true,
                              pointRadius: 4
                            }
                          ]
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: { legend: { labels: { color: 'rgba(255,255,255,0.7)', font: { size: 10 } } } },
                          scales: {
                            x: { grid: { display: false }, ticks: { color: 'rgba(255,255,255,0.45)', font: { size: 10 } } },
                            y: { grid: { color: 'rgba(255,255,255,0.04)' }, ticks: { color: 'rgba(255,255,255,0.45)' } }
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* Mesaj Trafiği Trendi */}
                  <div className="card" style={{ padding: '24px', borderRadius: '24px' }}>
                    <h3 style={{ margin: '0 0 16px', fontSize: '15px', color: '#fff', fontWeight: 700 }}>💬 Mesajlaşma Trafiği (Son 7 Gün)</h3>
                    <div style={{ height: '220px' }}>
                      <Line 
                        data={{
                          labels: dashboardData.communication?.messages?.trend?.map(t => t.date) || [],
                          datasets: [
                            {
                              label: 'Toplam Mesaj',
                              data: dashboardData.communication?.messages?.trend?.map(t => t.total) || [],
                              borderColor: 'rgba(255, 255, 255, 0.45)',
                              backgroundColor: 'transparent',
                              borderWidth: 2,
                              tension: 0.3,
                              pointRadius: 3
                            },
                            {
                              label: 'Cevaplanan Mesaj',
                              data: dashboardData.communication?.messages?.trend?.map(t => t.replied) || [],
                              borderColor: '#8b5cf6',
                              backgroundColor: 'rgba(139, 92, 246, 0.05)',
                              borderWidth: 3,
                              tension: 0.3,
                              fill: true,
                              pointRadius: 4
                            }
                          ]
                        }}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: { legend: { labels: { color: 'rgba(255,255,255,0.7)', font: { size: 10 } } } },
                          scales: {
                            x: { grid: { display: false }, ticks: { color: 'rgba(255,255,255,0.45)', font: { size: 10 } } },
                            y: { grid: { color: 'rgba(255,255,255,0.04)' }, ticks: { color: 'rgba(255,255,255,0.45)' } }
                          }
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Charts Row */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))', gap: '24px' }}>
                  {/* Sektörel Dağılım Grafiği */}
                  <div className="card" style={{ padding: '24px', borderRadius: '24px' }}>
                    <h3 style={{ margin: '0 0 16px', fontSize: '15px', color: '#fff', fontWeight: 700 }}>Sektörel Dağılım</h3>
                    <div style={{ height: '220px' }}>
                      <Bar 
                        data={{
                          labels: dashboardData.industry_breakdown.map(i => {
                            const labels = {
                              beauty_salon: '💄 Güzellik',
                              bridal_shop: '👗 Gelinlik',
                              dusakabin: '🚿 Duşakabin',
                              wedding_hall: '🏨 Düğün',
                              health_tourism: '🏥 Sağlık',
                              hotel: '🏨 Otel',
                              dental_clinic: '🦷 Diş Hekimi',
                              clinic: '🩺 Klinik',
                              ecommerce: '🛒 E-ticaret'
                            };
                            return labels[i.industry] || i.industry;
                          }),
                          datasets: [{
                            label: 'Şube Sayısı',
                            data: dashboardData.industry_breakdown.map(i => i.count),
                            backgroundColor: 'rgba(0, 242, 254, 0.75)',
                            hoverBackgroundColor: '#00f2fe',
                            borderRadius: 8,
                          }]
                        }} 
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: { legend: { display: false } },
                          scales: {
                            x: { grid: { display: false }, ticks: { color: 'rgba(255,255,255,0.45)', font: { size: 10 } } },
                            y: { grid: { color: 'rgba(255,255,255,0.04)' }, ticks: { color: 'rgba(255,255,255,0.45)', stepSize: 1 } }
                          }
                        }}
                      />
                    </div>
                  </div>

                  {/* Coğrafi Dağılım Grafiği */}
                  <div className="card" style={{ padding: '24px', borderRadius: '24px' }}>
                    <h3 style={{ margin: '0 0 16px', fontSize: '15px', color: '#fff', fontWeight: 700 }}>Coğrafi Dağılım (Şehirler)</h3>
                    <div style={{ height: '220px', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                      {dashboardData.city_breakdown.length === 0 ? (
                        <span className="text-muted" style={{ fontSize: '13px' }}>Şehir verisi bulunmuyor</span>
                      ) : (
                        <div style={{ width: '100%', height: '100%' }}>
                          <Doughnut
                            data={{
                              labels: dashboardData.city_breakdown.map(c => c.city),
                              datasets: [{
                                data: dashboardData.city_breakdown.map(c => c.count),
                                backgroundColor: ['#00f2fe', '#8b5cf6', '#f43f5e', '#fbbf24', '#10b981'],
                                borderWidth: 0
                              }]
                            }}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              plugins: {
                                legend: {
                                  position: 'right',
                                  labels: { color: 'rgba(255,255,255,0.7)', font: { size: 11 } }
                                }
                              }
                            }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Top Performing Branches & Recent Signups */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))', gap: '24px' }}>
                  {/* Top branches */}
                  <div className="card" style={{ padding: '24px', borderRadius: '24px' }}>
                    <h3 style={{ margin: '0 0 16px', fontSize: '15px', color: '#fff', fontWeight: 700 }}>En Çok İşlem Yapan Firmalar</h3>
                    <div className="table-container">
                      <table className="table" style={{ fontSize: '12px' }}>
                        <thead>
                          <tr>
                            <th>Şube Adı</th>
                            <th>Sektör / Şehir</th>
                            <th style={{ textAlign: 'center' }}>Müşteri</th>
                            <th style={{ textAlign: 'center' }}>Randevu</th>
                            <th style={{ textAlign: 'right' }}>Toplam Ciro</th>
                          </tr>
                        </thead>
                        <tbody>
                          {dashboardData.top_branches.map((b, idx) => (
                            <tr key={idx}>
                              <td><strong>{b.name}</strong></td>
                              <td>
                                <span style={{ color: 'var(--text-muted)' }}>
                                  {b.industry === 'beauty_salon' ? 'Güzellik' : b.industry} · {b.city || '-'}
                                </span>
                              </td>
                              <td style={{ textAlign: 'center' }}>{b.customer_count}</td>
                              <td style={{ textAlign: 'center' }}>{b.appointment_count}</td>
                              <td style={{ textAlign: 'right', fontWeight: 600, color: 'var(--primary)' }}>
                                ₺{Math.round(b.total_revenue).toLocaleString('tr-TR')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Recent registrations */}
                  <div className="card" style={{ padding: '24px', borderRadius: '24px' }}>
                    <h3 style={{ margin: '0 0 16px', fontSize: '15px', color: '#fff', fontWeight: 700 }}>Son Başvurular & Kayıtlar</h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {dashboardData.recent_registrations.map((r, idx) => {
                        const statusColors = {
                          pending: { bg: 'rgba(251, 191, 36, 0.12)', color: '#fbbf24', label: 'Bekliyor' },
                          approved: { bg: 'rgba(16, 185, 129, 0.12)', color: '#10b981', label: 'Onaylandı' },
                          rejected: { bg: 'rgba(244, 63, 94, 0.12)', color: '#f43f5e', label: 'Reddedildi' },
                          suspended: { bg: 'rgba(255, 255, 255, 0.05)', color: '#9ca3af', label: 'Pasif' }
                        };
                        const status = statusColors[r.status] || statusColors.pending;
                        return (
                          <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: 'rgba(255,255,255,0.015)', border: '1px solid var(--border)', borderRadius: '14px' }}>
                            <div>
                              <div style={{ color: '#fff', fontWeight: 600, fontSize: '13px' }}>{r.business_name}</div>
                              <div style={{ color: 'var(--text-muted)', fontSize: '11px', marginTop: '2px' }}>
                                {r.first_name} {r.last_name} · {new Date(r.created_at).toLocaleDateString('tr-TR')}
                              </div>
                            </div>
                            <span style={{ 
                              fontSize: '10px', 
                              fontWeight: 700, 
                              padding: '3px 8px', 
                              borderRadius: '99px', 
                              background: status.bg, 
                              color: status.color,
                              border: `1px solid ${status.color}33`
                            }}>
                              {status.label}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'registrations' && isPageEnabled('registrations') && (
          <div className="list">
            {registrations.length === 0 ? (
              <div className="empty">Bekleyen başvuru yok</div>
            ) : registrations.map(reg => (
              <div key={reg.id} className="item-card">
                <div className="item-info">
                  <div className="item-header">
                    <strong>{reg.business_name}</strong>
                    <span className={`badge ${reg.industry}`}>
                      {reg.industry === 'beauty_salon' ? 'Güzellik Salonu' : 
                       reg.industry === 'bridal_shop' ? 'Gelinlikçi' : 
                       reg.industry === 'dusakabin' ? 'Duşakabin' : reg.industry}
                    </span>
                    <BrandAssignSelect value={reg.brand} options={brandAccess.assignable} onChange={b => assignFirmBrand(reg.id, b)} />
                  </div>
                  <div className="item-details">
                    <span>{reg.first_name} {reg.last_name}</span>
                    <span>{reg.email}</span>
                    <span>{reg.phone || '-'}</span>
                  </div>
                  <div className="item-date">{new Date(reg.created_at).toLocaleDateString('tr-TR')}</div>
                </div>
                <div className="item-actions">
                  <button className="btn-approve" onClick={() => handleApprove(reg.id, 'registration')}>Onayla</button>
                  <button className="btn-reject" onClick={() => setRejectModal({ show: true, id: reg.id, type: 'registration' })}>Reddet</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'branches' && isPageEnabled('branches') && (
          <div className="list">
            {branchRequests.length === 0 ? (
              <div className="empty">Bekleyen şube talebi yok</div>
            ) : branchRequests.map(req => (
              <div key={req.id} className="item-card">
                <div className="item-info">
                  <div className="item-header">
                    <strong>{req.branch_name}</strong>
                    <span className="badge">{req.city || '-'}</span>
                  </div>
                  <div className="item-details">
                    <span>Mevcut Şube: {req.current_branch_name}</span>
                    <span>Talep Eden: {req.requester_name} {req.requester_last_name}</span>
                    <span>Adres: {req.address || '-'}</span>
                  </div>
                  <div className="item-date">{new Date(req.created_at).toLocaleDateString('tr-TR')}</div>
                </div>
                <div className="item-actions">
                  <button className="btn-approve" onClick={() => handleApprove(req.id, 'branch')}>Onayla</button>
                  <button className="btn-reject" onClick={() => setRejectModal({ show: true, id: req.id, type: 'branch' })}>Reddet</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── FIRMALAR TAB ── */}
        {activeTab === 'firms' && isPageEnabled('firms') && (
          <div>
            <div className="search-bar" style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <input
                type="text"
                className="search-input"
                style={{ flex: 1 }}
                placeholder="Firma ara (isim, sektör, sahip, email)..."
                value={firmSearch}
                onChange={e => setFirmSearch(e.target.value)}
                autoFocus
              />
              {firmSearch && <button className="search-clear" onClick={() => setFirmSearch('')} style={{ right: brandAccess.assignable.length > 1 ? '340px' : '190px' }}>✕</button>}
              {brandAccess.assignable.length > 1 && (
                <select
                  value={firmBrandFilter}
                  onChange={e => setFirmBrandFilter(e.target.value)}
                  style={{ padding: '10px 12px', borderRadius: 12, background: 'rgba(255,255,255,0.05)', color: '#fff', border: '1px solid rgba(255,255,255,0.1)', fontSize: 14 }}
                >
                  <option value="" style={{ color: '#000' }}>🎨 Tüm markalar</option>
                  {brandAccess.assignable.map(b => <option key={b.key} value={b.key} style={{ color: '#000' }}>{b.label}</option>)}
                </select>
              )}
              <button
                className="btn-add"
                style={{ whiteSpace: 'nowrap', backgroundColor: 'var(--tertiary)', color: '#fff', padding: '10px 16px', borderRadius: '12px', border: 'none', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px' }}
                onClick={() => setCreateDemoModal(true)}
              >
                🎭 Demo Hesap Oluştur
              </button>
            </div>
            {(() => {
              const q = firmSearch.toLowerCase();
              const brandFiltered = firmBrandFilter
                ? approvedBusinesses.filter(f => (f.brand || 'ritsa') === firmBrandFilter)
                : approvedBusinesses;
              const filtered = q
                ? brandFiltered.filter(f =>
                    (f.business_name || '').toLowerCase().includes(q) ||
                    (f.industry || '').toLowerCase().includes(q) ||
                    (f.first_name || '').toLowerCase().includes(q) ||
                    (f.last_name || '').toLowerCase().includes(q) ||
                    (f.email || '').toLowerCase().includes(q) ||
                    (f.phone || '').toLowerCase().includes(q) ||
                    (f.branch_name || '').toLowerCase().includes(q)
                  )
                : brandFiltered;
              return (
                <div className="list">
                  {filtered.length === 0 ? (
                    <div className="empty">{q ? 'Aramanızla eşleşen firma bulunamadı' : 'Onaylı firma yok'}</div>
                  ) : filtered.map(firm => {
                    const branch = approvedBranches.find(b => b.id === firm.branch_id);
                    const isDemoAccount = firm.is_demo === 1 || (branch && branch.is_demo === 1);
                    return (
                    <div key={firm.id} className="item-card">
                      <div className="item-info">
                        <div className="item-header">
                          <strong>{firm.business_name}</strong>
                          {isDemoAccount && (
                            <span className="badge" style={{ backgroundColor: 'rgba(var(--secondary-rgb),0.2)', color: '#c084fc', borderColor: 'rgba(var(--secondary-rgb),0.4)', fontWeight: 700 }}>
                              🎭 DEMO
                            </span>
                          )}
                          <span className={`badge ${firm.industry}`}>
                            {firm.industry === 'beauty_salon' ? '💄 Güzellik Salonu' : 
                             firm.industry === 'bridal_shop' ? '👗 Gelinlikçi' : 
                             firm.industry === 'dusakabin' ? '🚿 Duşakabin' : 
                             firm.industry === 'wedding_hall' ? '🏨 Düğün Salonu' : 
                             firm.industry === 'health_tourism' ? '🏥 Sağlık Turizmi' : 
                             firm.industry === 'hotel' ? '🏨 Otelcilik' : 
                             firm.industry === 'dental_clinic' ? '🦷 Diş Hekimliği' : 
                             firm.industry === 'clinic' ? '🩺 Klinik & Hastane' : 
                             firm.industry === 'ecommerce' ? '🛒 E-ticaret' : 
                             firm.industry === 'automotive' ? '🚗 Otomotiv' :
                             firm.industry === 'veterinary' ? '🐾 Veteriner' :
                             firm.industry === 'education' ? '📚 Eğitim' : firm.industry}
                          </span>
                          {branch && (
                            <span className={`badge ${branch.is_active ? 'active' : 'inactive'}`}>
                              {branch.is_active ? 'Aktif' : 'Pasif'}
                            </span>
                          )}
                          <BrandAssignSelect value={firm.brand} options={brandAccess.assignable} onChange={b => assignFirmBrand(firm.id, b)} />
                        </div>
                        <div className="item-details">
                          <span>👤 {firm.first_name} {firm.last_name}</span>
                          <span>📧 {firm.email}</span>
                          <span>📞 {firm.phone || '-'}</span>
                          <span>🏢 {firm.branch_name || '-'}</span>
                          {branch && <span>📍 {branch.city || '-'}</span>}
                        </div>
                        <div className="item-date">Kayıt: {new Date(firm.created_at).toLocaleDateString('tr-TR')}</div>
                      </div>
                      <div className="item-actions">
                        {branch && (<>
                          <button className="btn-ai" onClick={async () => {
                            setActiveTab('ai-center');
                            const found = aiOverview.find(b => b.branch_id === branch.id);
                            const branchData = found || { branch_id: branch.id, branch_name: branch.name, industry: branch.industry, is_enabled: false, provider: null, model: null };
                            await handleSelectAIBranch(branchData);
                            setSelectedAIBranch(branchData);
                          }}>
                            🤖 AI Center
                          </button>
                          <button className="btn-edit" onClick={() => handleEditBranch(branch.id)}>Düzenle</button>
                          <button className="btn-toggle" onClick={() => handleToggleBranch(branch.id)}>
                            {branch.is_active ? 'Pasife Al' : 'Aktif Et'}
                          </button>
                          <button className="btn-delete" onClick={() => handleDeleteBranch(branch.id)}>Sil</button>
                        </>)}
                        <button className="btn-toggle" onClick={() => handleToggleBusiness(firm.id)} style={branch ? { marginLeft: 8 } : {}}>
                          {firm.status === 'suspended' ? 'Firma Aktif Et' : 'Firma Pasife Al'}
                        </button>
                      </div>
                    </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
        )}

        {activeTab === 'industries' && isPageEnabled('industries') && (() => {
          const getModules = (ind) => {
            const setting = industrySettings.find(i => i.industry === ind);
            let current = {};
            if (setting?.modules) {
              try {
                current = typeof setting.modules === 'string' ? JSON.parse(setting.modules) : setting.modules;
              } catch (e) { current = {}; }
            }
            
            const defaults = {};
            Object.entries(MODULE_DEFINITIONS).forEach(([key, def]) => {
              const isRelevant = def.industries === null || def.industries.includes(ind);
              defaults[key] = isRelevant ? true : false;
            });

            return { ...defaults, ...current };
          };

          const handleModuleToggle = async (industryKey, moduleKey, value) => {
            const current = getModules(industryKey);
            const updated = { ...current, [moduleKey]: value };
            await handleUpdateModules(industryKey, updated);
          };

          // Show all modules for each industry in the admin panel so any module can be enabled/disabled
          const getRelevantModules = (industryKey) =>
            Object.entries(MODULE_DEFINITIONS);

          return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="info-box" style={{ margin: 0, flex: 1 }}>
                  Her sektörün açık/kapalı durumunu ve o sektörde görünecek sayfaları buradan yönetebilirsiniz.
                </div>
                <button className="btn-ai" onClick={() => {
                  setIndustryForm({ industry: '', display_name: '', description: '', modules: {} });
                  setNewIndustryModal(true);
                }} style={{ padding: '10px 20px', marginLeft: '12px', whiteSpace: 'nowrap' }}>
                  + Yeni Sektör Ekle
                </button>
              </div>

              {industrySettings.map(ind => {
                const modules = getModules(ind.industry);
                const isOpen = selectedIndustry === ind.industry;
                const relevantModules = getRelevantModules(ind.industry);
                const sharedModules = relevantModules.filter(([, def]) => def.industries === null);
                const exclusiveModules = relevantModules.filter(([, def]) => def.industries !== null);

                return (
                  <div key={ind.id} className="industry-card">
                    {/* Industry header row */}
                    <div className="industry-header" onClick={() => {
                      // Kaydedilmemiş prompt değişikliği varken sektör değiştirmeyi engelle
                      if (industryPromptDirty && !window.confirm('Kaydedilmemiş prompt değişikliğiniz var. Yine de çıkmak istiyor musunuz?')) {
                        return;
                      }
                      const newSelected = isOpen ? null : ind.industry;
                      setSelectedIndustry(newSelected);
                      setIndustryPromptDirty(false);
                      setIndustryPromptChannel('');
                      if (newSelected) {
                        fetchIndustryAutomations(newSelected);
                        fetchIndustryPrompt(newSelected, '');
                      }
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <span className="industry-icon">
                          {ind.industry === 'beauty_salon' ? '💄' : 
                           ind.industry === 'bridal_shop' ? '👗' : 
                           ind.industry === 'dusakabin' ? '🚿' : 
                           ind.industry === 'health_tourism' ? '🏥' : 
                           ind.industry === 'hotel' ? '🏨' : 
                           ind.industry === 'dental_clinic' ? '🦷' : 
                           ind.industry === 'clinic' ? '🩺' : 
                           ind.industry === 'ecommerce' ? '🛒' : '🏢'}
                        </span>
                        <div>
                          <div style={{ fontWeight: 600, fontSize: '15px', color: '#FFFFFF' }}>{ind.display_name}</div>
                          <div style={{ fontSize: '12px', color: '#9CA3AF', marginTop: '2px' }}>{ind.description || ''}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }} onClick={e => e.stopPropagation()}>
                          {/* Sektör aktif/pasif toggle */}
                        <label className="toggle-switch" onClick={e => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={!!ind.enabled}
                            onChange={e => handleToggleIndustry(ind.industry, e.target.checked)}
                          />
                          <span className="toggle-slider"></span>
                        </label>
                        <span style={{ color: ind.enabled ? '#10b981' : '#94a3b8', fontSize: '13px', fontWeight: 500 }}>
                            {ind.enabled ? 'Aktif' : 'Pasif'}
                          </span>
                          <button onClick={() => {
                            if (confirm(`"${ind.display_name}" sektörünü silmek istediğinize emin misiniz?`)) {
                              handleDeleteIndustry(ind.id);
                            }
                          }} className="btn-delete" style={{ padding: '6px 12px', fontSize: '12px', marginLeft: '6px' }}>
                            Sil
                          </button>
                        <span style={{ color: '#94a3b8', fontSize: '18px', transition: 'transform .2s', transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}>▾</span>
                      </div>
                    </div>

                    {/* Expanded module panel */}
                    {isOpen && (
                      <div className="module-expand">
                        {/* Shared modules */}
                        <div className="module-group-label">🔗 Ortak Sayfalar</div>
                        <div className="modules-grid">
                          {sharedModules.map(([key, def]) => {
                            const mobileSidebar = ind.mobile_sidebar_modules?.[key] === true;
                            const mobileNavbar = ind.mobile_navbar_modules?.[key] === true;
                            return (
                            <div key={key} className="module-item" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                <input
                                  type="checkbox"
                                  checked={modules[key] !== false}
                                  onChange={e => handleModuleToggle(ind.industry, key, e.target.checked)}
                                />
                                <span className="module-label" style={{ flex: 1 }}>{def.label} (Web)</span>
                                <span className={`module-status ${modules[key] !== false ? 'on' : 'off'}`}>
                                  {modules[key] !== false ? 'Açık' : 'Kapalı'}
                                </span>
                              </div>
                              <div style={{ display: 'flex', gap: '16px', paddingLeft: '24px' }}>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#94a3b8' }}>
                                  <input 
                                    type="checkbox" 
                                    checked={mobileSidebar} 
                                    onChange={async (e) => {
                                      const current = ind.mobile_sidebar_modules || {};
                                      const updated = { ...current, [key]: e.target.checked };
                                      await axios.put(`/api/admin/industry-modules/${ind.industry}`, { mobile_sidebar_modules: updated }, { headers: { Authorization: `Bearer ${token}` } });
                                      fetchData();
                                    }}
                                  /> Mobil Sidebar
                                </label>
                                <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#94a3b8' }}>
                                  <input 
                                    type="checkbox" 
                                    checked={mobileNavbar} 
                                    onChange={async (e) => {
                                      const current = ind.mobile_navbar_modules || {};
                                      const updated = { ...current, [key]: e.target.checked };
                                      await axios.put(`/api/admin/industry-modules/${ind.industry}`, { mobile_navbar_modules: updated }, { headers: { Authorization: `Bearer ${token}` } });
                                      fetchData();
                                    }}
                                  /> Mobil Navbar
                                </label>
                              </div>
                            </div>
                          )})}
                        </div>

                        {/* Exclusive modules */}
                        {exclusiveModules.length > 0 && (
                          <>
                            <div className="module-group-label" style={{ marginTop: '16px' }}>⭐ Sektöre Özel Sayfalar</div>
                            <div className="modules-grid">
                              {exclusiveModules.map(([key, def]) => {
                                const mobileSidebar = ind.mobile_sidebar_modules?.[key] === true;
                                const mobileNavbar = ind.mobile_navbar_modules?.[key] === true;
                                return (
                                <div key={key} className="module-item exclusive" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                    <input
                                      type="checkbox"
                                      checked={modules[key] !== false}
                                      onChange={e => handleModuleToggle(ind.industry, key, e.target.checked)}
                                    />
                                    <span className="module-label" style={{ flex: 1 }}>{def.label} (Web)</span>
                                    <span className={`module-status ${modules[key] !== false ? 'on' : 'off'}`}>
                                      {modules[key] !== false ? 'Açık' : 'Kapalı'}
                                    </span>
                                  </div>
                                  <div style={{ display: 'flex', gap: '16px', paddingLeft: '24px' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#94a3b8' }}>
                                      <input 
                                        type="checkbox" 
                                        checked={mobileSidebar} 
                                        onChange={async (e) => {
                                          const current = ind.mobile_sidebar_modules || {};
                                          const updated = { ...current, [key]: e.target.checked };
                                          await axios.put(`/api/admin/industry-modules/${ind.industry}`, { mobile_sidebar_modules: updated }, { headers: { Authorization: `Bearer ${token}` } });
                                          fetchData();
                                        }}
                                      /> Mobil Sidebar
                                    </label>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#94a3b8' }}>
                                      <input 
                                        type="checkbox" 
                                        checked={mobileNavbar} 
                                        onChange={async (e) => {
                                          const current = ind.mobile_navbar_modules || {};
                                          const updated = { ...current, [key]: e.target.checked };
                                          await axios.put(`/api/admin/industry-modules/${ind.industry}`, { mobile_navbar_modules: updated }, { headers: { Authorization: `Bearer ${token}` } });
                                          fetchData();
                                        }}
                                      /> Mobil Navbar
                                    </label>
                                  </div>
                                </div>
                              )})}
                            </div>
                          </>
                        )}
                        {/* Sektör AI prompt standardı */}
                        {(() => {
                          const pd = industryPromptData[ind.industry];
                          const len = industryPromptText.length;
                          const counterColor = len > 6000 ? '#F87171' : len > 3000 ? '#FBBF24' : '#9CA3AF';
                          return (
                            <div style={{ marginTop: '24px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '16px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
                                <div className="module-group-label" style={{ margin: 0 }}>🤖 Sektör AI Prompt Standardı</div>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                  <button className="btn-ai" style={{ padding: '6px 12px', fontSize: '12px' }}
                                    onClick={() => fetchIndustryPromptHistory(ind.industry)}>🕐 Geçmiş</button>
                                  <button className="btn-ai" style={{ padding: '6px 12px', fontSize: '12px' }}
                                    onClick={() => {
                                      if (!pd?.factory_prompt) { alert('Bu sektör için fabrika metni tanımlı değil.'); return; }
                                      setIndustryPromptText(pd.factory_prompt);
                                      setIndustryPromptDirty(true);
                                    }}>↺ Fabrika ayarı</button>
                                  <button className="btn-ai" style={{ padding: '6px 12px', fontSize: '12px', background: '#10B981' }}
                                    disabled={industryPromptSaving || !industryPromptDirty}
                                    onClick={() => handleSaveIndustryPrompt(ind.industry)}>
                                    {industryPromptSaving ? 'Kaydediliyor...' : '💾 Kaydet'}
                                  </button>
                                </div>
                              </div>

                              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '10px' }}>
                                {[
                                  { value: '', label: '🌐 Genel' },
                                  { value: 'whatsapp', label: '💬 WhatsApp' },
                                  { value: 'instagram', label: '📸 Instagram' },
                                  { value: 'web_chat', label: '🖥️ Web Chat' },
                                  { value: 'sms', label: '📱 SMS' }
                                ].map(ch => (
                                  <button
                                    key={ch.value || 'general'}
                                    type="button"
                                    onClick={() => selectIndustryPromptChannel(ind.industry, ch.value)}
                                    style={{
                                      padding: '5px 12px', borderRadius: '8px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                                      border: `1px solid ${industryPromptChannel === ch.value ? '#8B5CF6' : 'var(--border)'}`,
                                      background: industryPromptChannel === ch.value ? 'rgba(139,92,246,0.15)' : 'rgba(255,255,255,0.03)',
                                      color: industryPromptChannel === ch.value ? '#C4B5FD' : '#9CA3AF'
                                    }}
                                  >
                                    {ch.label}
                                  </button>
                                ))}
                              </div>

                              {industryPromptChannel && !pd?.has_channel_override && (
                                <div style={{ fontSize: '12px', color: '#FBBF24', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)', borderRadius: '8px', padding: '8px 12px', marginBottom: '10px' }}>
                                  ⚠️ Bu kanal için henüz özel bir standart kaydedilmedi — aşağıda <strong>genel</strong> standart taslak olarak gösteriliyor. Kaydederseniz bu kanala özel, genelden bağımsız bir standart oluşur.
                                </div>
                              )}

                              <div style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '8px' }}>
                                Bu metin, sektördeki <strong style={{ color: '#fff' }}>katmanlı moddaki</strong> tüm şubelerin
                                {industryPromptChannel ? <> <strong style={{ color: '#fff' }}>{{ whatsapp: 'WhatsApp', instagram: 'Instagram', web_chat: 'Web Chat', sms: 'SMS' }[industryPromptChannel]}</strong> kanalı için</> : ''} promptunun üst katmanıdır.
                                Kaydettiğinizde <strong style={{ color: '#fff' }}>anında</strong> canlıya yansır. Firmaların kendi ekledikleri bilgiler korunur.
                                {!industryPromptChannel && ' Kanal-özel bir standart tanımlanmamışsa bu genel standart tüm kanallarda kullanılır.'}
                              </div>

                              <textarea
                                rows={20}
                                value={industryPromptText}
                                onChange={e => { setIndustryPromptText(e.target.value); setIndustryPromptDirty(true); }}
                                style={{ width: '100%', background: 'rgba(0,0,0,0.25)', border: '1px solid var(--border)', borderRadius: '10px',
                                         padding: '12px', color: '#E5E7EB', fontFamily: 'monospace', fontSize: '12.5px', lineHeight: 1.5, resize: 'vertical' }}
                                placeholder="Bu sektörün standart AI promptu..."
                              />

                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '6px', fontSize: '12px', flexWrap: 'wrap', gap: '8px' }}>
                                <span style={{ color: counterColor }}>
                                  {len} karakter · ~{Math.ceil(len / 4)} token
                                  {len > 6000 ? ' — çok uzun, maliyet ve talimat seyrelmesi riski' : len > 3000 ? ' — uzun' : ''}
                                </span>
                                <span style={{ color: '#9CA3AF' }}>
                                  Sürüm: <strong style={{ color: '#fff' }}>v{pd?.version || 0}</strong>
                                  {' · '}Bu sektörde <strong style={{ color: '#fff' }}>{pd?.branch_count ?? 0}</strong> şube
                                  {' · '}<strong style={{ color: pd?.layered_count ? '#34D399' : '#9CA3AF' }}>{pd?.layered_count ?? 0}</strong> tanesi bu standardı kullanıyor
                                </span>
                              </div>

                              {pd && pd.branch_count > 0 && pd.layered_count === 0 && (
                                <div style={{ marginTop: '8px', padding: '8px 12px', background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.25)', borderRadius: '8px', fontSize: '12px', color: '#FBBF24' }}>
                                  ⚠️ Bu sektördeki şubelerin hiçbiri henüz katmanlı modda değil — buradaki düzenleme onları <strong>etkilemez</strong>. Firmalar kendi AI ekranlarından geçiş yapabilir.
                                </div>
                              )}

                              {/* Geçmiş listesi */}
                              {showPromptHistory && (
                                <div style={{ marginTop: '12px', background: 'rgba(0,0,0,0.2)', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                    <strong style={{ fontSize: '13px', color: '#fff' }}>Sürüm Geçmişi</strong>
                                    <button onClick={() => setShowPromptHistory(false)} style={{ background: 'none', border: 'none', color: '#9CA3AF', cursor: 'pointer' }}>✕</button>
                                  </div>
                                  {industryPromptHistory.length === 0 ? (
                                    <div style={{ color: '#6B7280', fontSize: '12px', fontStyle: 'italic' }}>Henüz kayıtlı sürüm yok.</div>
                                  ) : industryPromptHistory.map(h => (
                                    <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', padding: '6px 0', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                                      <div style={{ minWidth: 0, flex: 1 }}>
                                        <span style={{ color: '#fff', fontSize: '12px' }}>v{h.version} · {new Date(h.created_at).toLocaleString('tr-TR')} · {h.length} kr</span>
                                        <div style={{ color: '#6B7280', fontSize: '11px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{h.preview}</div>
                                      </div>
                                      <button className="btn-ai" style={{ padding: '4px 10px', fontSize: '11px' }}
                                        onClick={() => handleRestoreIndustryPrompt(ind.industry, h.id)}>Geri yükle</button>
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Şube önizlemesi */}
                              <div style={{ marginTop: '12px', background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px' }}>
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                                  <strong style={{ fontSize: '13px', color: '#fff' }}>Şube önizlemesi:</strong>
                                  <select value={previewBranchId}
                                    onChange={e => { setPreviewBranchId(e.target.value); handlePreviewComposed(ind.industry, e.target.value); }}
                                    style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid var(--border)', borderRadius: '8px', padding: '6px 10px', color: '#E5E7EB', fontSize: '12px' }}>
                                    <option value="">Şube seçin...</option>
                                    {industryBranches.map(b => (
                                      <option key={b.id} value={b.id}>{b.name} ({b.prompt_mode === 'layered' ? 'katmanlı' : 'eski mod'})</option>
                                    ))}
                                  </select>
                                </div>

                                {promptPreview && (
                                  <div style={{ marginTop: '10px' }}>
                                    {promptPreview.prompt_mode !== 'layered' && (
                                      <div style={{ padding: '8px 12px', background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)', borderRadius: '8px', fontSize: '12px', color: '#F87171', marginBottom: '8px' }}>
                                        ⚠️ Bu şube <strong>eski modda</strong> — yukarıdaki standardı KULLANMIYOR. Aşağıdaki metin şubenin kendi tek parça promptudur.
                                      </div>
                                    )}
                                    <div style={{ fontSize: '11.5px', color: '#9CA3AF', marginBottom: '6px' }}>
                                      Standart: {promptPreview.standard_length} kr · Firma eki: {promptPreview.company_length} kr · Toplam: <strong style={{ color: '#fff' }}>{promptPreview.total_length} kr (~{Math.ceil(promptPreview.total_length / 4)} token)</strong>
                                      {promptPreview.missing_standard && <span style={{ color: '#FBBF24' }}> · ⚠️ standart bulunamadı, yedek kullanıldı</span>}
                                    </div>
                                    <pre style={{ background: 'rgba(0,0,0,0.3)', padding: '10px', borderRadius: '8px', fontSize: '11.5px', color: '#D1D5DB', maxHeight: '260px', overflow: 'auto', whiteSpace: 'pre-wrap', margin: 0 }}>
                                      {promptPreview.composed_prompt}
                                    </pre>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })()}

                        {/* Industry automation templates */}
                        <div style={{ marginTop: '24px', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '16px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <div className="module-group-label" style={{ margin: 0 }}>🚀 Hazır Otomasyon Şablonları</div>
                            <button className="btn-ai" onClick={() => handleOpenAutomationTemplateModal(ind.industry)} style={{ padding: '6px 12px', fontSize: '12px' }}>
                              + Şablon Ekle
                            </button>
                          </div>
                          
                          {loadingAutomations && !industryAutomations[ind.industry] ? (
                            <div style={{ color: '#9CA3AF', fontSize: '13px' }}>Yükleniyor...</div>
                          ) : !industryAutomations[ind.industry] || industryAutomations[ind.industry].length === 0 ? (
                            <div style={{ color: '#6B7280', fontSize: '13px', fontStyle: 'italic', padding: '8px 0' }}>
                              Bu sektör için henüz hazır otomasyon şablonu eklenmemiş. Yeni işletmeler kurulduğunda bu liste boş gelecektir.
                            </div>
                          ) : (
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '12px' }}>
                              {industryAutomations[ind.industry].map(tmpl => (
                                <div key={tmpl.id} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid var(--border)', borderRadius: '12px', padding: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                    <strong style={{ color: '#fff', fontSize: '13px' }}>{tmpl.name}</strong>
                                    <div style={{ display: 'flex', gap: '4px' }}>
                                      <button onClick={() => handleOpenAutomationTemplateModal(ind.industry, tmpl)} style={{ background: 'none', border: 'none', color: '#60A5FA', cursor: 'pointer', fontSize: '12px', padding: '2px' }}>Düzenle</button>
                                      <span style={{ color: '#374151' }}>|</span>
                                      <button onClick={() => handleDeleteAutomationTemplate(ind.industry, tmpl.id)} style={{ background: 'none', border: 'none', color: '#FCA5A5', cursor: 'pointer', fontSize: '12px', padding: '2px' }}>Sil</button>
                                    </div>
                                  </div>
                                  <div style={{ fontSize: '11px', color: '#9CA3AF', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                    <span>Tetikleyici: <strong style={{ color: '#fff' }}>{
                                      tmpl.trigger_type === 'appointment_before' ? 'Randevu Öncesi' :
                                      tmpl.trigger_type === 'birthday' ? 'Doğum Günü' :
                                      tmpl.trigger_type === 'no_visit' ? 'Ziyaret Etmeyen' :
                                      tmpl.trigger_type === 'new_order' ? 'Yeni Sipariş' :
                                      tmpl.trigger_type === 'payment_reminder' ? 'Ödeme Hatırlatma' :
                                      tmpl.trigger_type === 'package_ending' ? 'Paket Bitiyor' : tmpl.trigger_type
                                    }</strong></span>
                                    {tmpl.trigger_value !== null && <span>Değer: <strong style={{ color: '#fff' }}>{tmpl.trigger_value}</strong></span>}
                                    <span>Kanal: <strong style={{ color: '#fff' }}>{tmpl.channel}</strong></span>
                                  </div>
                                  <div style={{ background: 'rgba(0,0,0,0.2)', padding: '8px', borderRadius: '6px', fontSize: '12px', color: '#D1D5DB', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>
                                    {tmpl.template}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}

        {activeTab === 'ai-center' && isPageEnabled('ai-center') && (
          <>
            {/* Ritsa Asist - Ortak/Paylaşımlı WAHA Bağlantısı */}
            <div style={{ marginBottom: 20, padding: '18px 20px', background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <span style={{ fontSize: 20 }}>🤖</span>
                <div style={{ fontWeight: 700, color: '#34d399', fontSize: 15 }}>Ritsa Asist — Ortak WAHA Bağlantısı</div>
              </div>
              <div style={{ fontSize: 12.5, color: '#9CA3AF', marginBottom: 14, lineHeight: 1.5 }}>
                Tüm işletme sahipleri, kendi işletmeleri hakkında soru sormak için AYNI bu tek numaraya
                yazar (artık şubelerin kendi WhatsApp numaraları üzerinden ÇALIŞMAZ). Burada girilen
                bağlantı tüm şubeler için ortaktır, şubeye özel değildir.
              </div>
              {ritsaAsistWahaLoading ? (
                <div className="loading"><div className="loading-spinner"></div></div>
              ) : (
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                  <div style={{ flex: '1 1 260px' }}>
                    <label style={{ fontSize: 12, color: '#9CA3AF', display: 'block', marginBottom: 4 }}>WAHA URL</label>
                    <input
                      type="text"
                      placeholder="https://waha.ornek.com"
                      value={ritsaAsistWahaForm.waha_url}
                      onChange={e => setRitsaAsistWahaForm(f => ({ ...f, waha_url: e.target.value }))}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.2)', color: '#fff' }}
                    />
                  </div>
                  <div style={{ flex: '1 1 200px' }}>
                    <label style={{ fontSize: 12, color: '#9CA3AF', display: 'block', marginBottom: 4 }}>Oturum (Session) Adı</label>
                    <input
                      type="text"
                      placeholder="default"
                      value={ritsaAsistWahaForm.waha_session}
                      onChange={e => setRitsaAsistWahaForm(f => ({ ...f, waha_session: e.target.value }))}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.2)', color: '#fff' }}
                    />
                  </div>
                  <div style={{ flex: '1 1 220px' }}>
                    <label style={{ fontSize: 12, color: '#9CA3AF', display: 'block', marginBottom: 4 }}>
                      API Anahtarı {ritsaAsistWaha.waha_api_key_set && <span style={{ color: '#34d399' }}>(kayıtlı ✓ — boş bırakılırsa korunur)</span>}
                    </label>
                    <input
                      type="password"
                      placeholder={ritsaAsistWaha.waha_api_key_set ? '••••••••' : ''}
                      value={ritsaAsistWahaForm.waha_api_key}
                      onChange={e => setRitsaAsistWahaForm(f => ({ ...f, waha_api_key: e.target.value }))}
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(255,255,255,0.1)', background: 'rgba(0,0,0,0.2)', color: '#fff' }}
                    />
                  </div>
                  <button
                    onClick={handleSaveRitsaAsistWaha}
                    disabled={ritsaAsistWahaSaving}
                    style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg,#10b981,#059669)', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
                  >
                    {ritsaAsistWahaSaving ? 'Kaydediliyor...' : 'Kaydet'}
                  </button>
                </div>
              )}
              <div style={{ fontSize: 11, color: '#6B7280', marginTop: 10 }}>
                WAHA panelinden bu numaranın webhook URL'sini <code style={{ color: '#9CA3AF' }}>https://crm.ritsaai.com/webhook/waha-ritsa-asist</code> olarak ayarlayın.
              </div>
            </div>

            {/* Şube Seçili Banner */}
            {selectedAIBranch && (
              <div style={{ display:'flex', alignItems:'center', gap: 12, marginBottom: 16, padding: '12px 18px', background: 'rgba(var(--secondary-rgb),0.08)', border: '1px solid rgba(var(--secondary-rgb),0.2)', borderRadius: 12 }}>
                <span style={{ fontSize: 22 }}>🤖</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, color: '#a78bfa', fontSize: 15 }}>{selectedAIBranch.branch_name}</div>
                  <div style={{ fontSize: 12, color: '#6B7280' }}>AI Center — tam yönetim modu</div>
                </div>
                {aiBranchStats && (
                  <div style={{ display:'flex', gap: 16, fontSize: 12, color: '#9CA3AF' }}>
                    <span>🤖 {aiBranchStats.agents?.total || 0} Agent</span>
                    <span>📞 {aiBranchStats.calls?.total || 0} Çağrı</span>
                    <span>📅 Bugün: {aiBranchStats.calls?.today || 0}</span>
                  </div>
                )}
                <button onClick={() => { setSelectedAIBranch(null); setActiveTab('firms'); }} style={{ padding: '7px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#9CA3AF', cursor: 'pointer', fontSize: 13 }}>
                  ← Firmalar
                </button>
              </div>
            )}

            <div style={{ display: 'flex', gap: 20, minHeight: 500, flexWrap: 'wrap' }}>
              {/* SOL: Şube Listesi */}
              <div style={{ flex: '1 1 300px', maxWidth: 300, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#6B7280', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom: 4 }}>Şubeler ({aiOverview.length})</div>
                {aiOverview.length === 0 ? (
                  <div className="empty">Şube bulunamadı</div>
                ) : aiOverview.map(br => (
                  <div
                    key={br.branch_id}
                    onClick={() => handleSelectAIBranch(br)}
                    style={{
                      padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                      background: selectedAIBranch?.branch_id === br.branch_id ? 'rgba(139,92,246,0.12)' : 'rgba(17,17,24,0.6)',
                      border: selectedAIBranch?.branch_id === br.branch_id ? '1px solid rgba(139,92,246,0.5)' : '1px solid rgba(255,255,255,0.06)',
                      transition: 'all 0.15s',
                    }}
                  >
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 6 }}>
                      <div style={{ fontWeight: 600, color: '#fff', fontSize: 13 }}>{br.branch_name}</div>
                      <span className={`badge ${br.is_enabled ? 'active' : 'inactive'}`} style={{ fontSize: 10 }}>
                        {br.is_enabled ? '🟢' : '🔴'}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: '#6B7280', display:'flex', gap: 10 }}>
                      <span>🤖 {br.agent_count || 0}</span>
                      <span>📞 {br.total_calls || 0}</span>
                      <span style={{ color: br.provider === 'gemini' ? '#34D399' : '#60A5FA' }}>
                        {br.provider === 'gemini' ? 'Gemini' : 'OpenAI'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* SAĞ: Yönetim Paneli */}
              <div style={{ flex: 1, minWidth: 0 }}>
                {!selectedAIBranch ? (
                  <div className="empty" style={{ paddingTop: 100 }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>🤖</div>
                    <div>Sol taraftan bir şube seçin</div>
                    <div style={{ fontSize: 12, color: '#6B7280', marginTop: 4 }}>Agent oluşturma, AI ayarları ve çağrı geçmişi burada görünecek</div>
                  </div>
                ) : (
                  <AdminAICenterPanel branchId={selectedAIBranch.branch_id} branchName={selectedAIBranch.branch_name} />
                )}
              </div>
            </div>
          </>
        )}

        {activeTab === 'reports' && isPageEnabled('reports') && (
          <div>
            {selectedReportBranch ? (
              <div>
                <button
                  onClick={() => setSelectedReportBranch(null)}
                  style={{ marginBottom: 16, padding: '7px 14px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#9CA3AF', cursor: 'pointer', fontSize: 13 }}
                >
                  ← Tüm Firmalar
                </button>
                <ReportsView
                  apiBase={`/api/admin/branches/${selectedReportBranch.branch_id}/reports`}
                  token={token}
                  title={`📈 ${selectedReportBranch.branch_name}`}
                />
              </div>
            ) : reportsLoading || !reportsData ? (
              <div className="loading"><div className="loading-spinner"></div></div>
            ) : (
              <div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 12 }}>
                  <h2 style={{ fontSize: 18, fontWeight: 700, color: '#fff', margin: 0 }}>📈 Tüm Firmalar — Genel Bakış</h2>
                  <div style={{ display: 'flex', gap: 4, background: 'rgba(255,255,255,0.04)', padding: 4, borderRadius: 10 }}>
                    {[['today', 'Bugün'], ['week', 'Bu Hafta'], ['month', 'Bu Ay']].map(([k, l]) => (
                      <button
                        key={k}
                        onClick={() => handleReportsPeriodChange(k)}
                        style={{
                          padding: '7px 16px', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: 'pointer',
                          background: reportsPeriod === k ? 'linear-gradient(135deg,#00c2fe,#3b82f6)' : 'transparent',
                          color: reportsPeriod === k ? '#fff' : '#9CA3AF'
                        }}
                      >{l}</button>
                    ))}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
                  {[
                    ['Gelen Mesaj', reportsData.totals.messages_received, '💬', '#00f2fe'],
                    ['Giden Mesaj', reportsData.totals.messages_sent, '📤', '#60a5fa'],
                    ['Yeni Müşteri', reportsData.totals.new_customers, '👤', '#8b5cf6'],
                    ['Oluşturulan Randevu', reportsData.totals.appointments_created, '📅', '#fbbf24'],
                    ['Tamamlanan Randevu', reportsData.totals.appointments_completed, '✅', '#10b981'],
                    ['Ciro', `₺${Number(reportsData.totals.revenue || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`, '💰', '#34d399'],
                    ['Kampanya Mesajı', reportsData.totals.campaign_messages_sent, '📣', '#ec4899']
                  ].map(([label, value, icon, color]) => (
                    <div key={label} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 16, padding: 18, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div style={{ display: 'flex', flexDirection: 'column' }}>
                        <span style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 6 }}>{label}</span>
                        <span style={{ fontSize: 22, fontWeight: 700, color: '#fff' }}>{value}</span>
                      </div>
                      <div style={{ width: 36, height: 36, borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0, background: `${color}22`, color }}>{icon}</div>
                    </div>
                  ))}
                </div>

                <div style={{ fontSize: 13, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>
                  Firma Firma Kırılım ({reportsData.by_branch.length})
                </div>
                <div style={{ overflowX: 'auto', background: 'rgba(17,17,24,0.6)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.06)' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
                        {['Firma', 'Gelen Mesaj', 'Randevu', 'Yeni Müşteri', 'Ciro', ''].map(h => (
                          <th key={h} style={{ padding: '10px 12px', textAlign: 'left', color: '#6B7280', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {reportsData.by_branch.map(b => (
                        <tr key={b.branch_id} style={{ borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
                          <td style={{ padding: '9px 12px', color: '#E5E7EB', fontWeight: 600 }}>{b.branch_name}</td>
                          <td style={{ padding: '9px 12px', color: '#9CA3AF' }}>{b.messages_received}</td>
                          <td style={{ padding: '9px 12px', color: '#9CA3AF' }}>{b.appointments_created}</td>
                          <td style={{ padding: '9px 12px', color: '#9CA3AF' }}>{b.new_customers}</td>
                          <td style={{ padding: '9px 12px', color: '#9CA3AF' }}>₺{Number(b.revenue || 0).toLocaleString('tr-TR', { maximumFractionDigits: 0 })}</td>
                          <td style={{ padding: '9px 12px' }}>
                            <button
                              onClick={() => setSelectedReportBranch({ branch_id: b.branch_id, branch_name: b.branch_name })}
                              style={{ padding: '5px 12px', background: 'rgba(59,130,246,0.12)', border: '1px solid rgba(59,130,246,0.25)', borderRadius: 7, color: '#60a5fa', cursor: 'pointer', fontSize: 12 }}
                            >Detay →</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'brands' && isPageEnabled('brands') && (
          <BrandSettingsPanel token={token} />
        )}

        {activeTab === 'announcements' && isPageEnabled('announcements') && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div className="info-box" style={{ margin: 0, flex: 1, marginRight: 12 }}>
                Burada eklediğiniz duyuru/güncellemeler TÜM firmaların CRM'ine yansır —
                ilk girişlerinde bir kez popup olarak, ve "Güncellemeler" sayfalarında
                kalıcı olarak görünür.
              </div>
              <button className="btn-ai" onClick={() => openAnnouncementModal()} style={{ padding: '10px 20px', whiteSpace: 'nowrap' }}>
                + Yeni Duyuru
              </button>
            </div>

            {announcementsLoading ? (
              <div className="loading"><div className="loading-spinner"></div></div>
            ) : announcements.length === 0 ? (
              <div className="empty">Henüz duyuru yok</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {announcements.map(item => (
                  <div key={item.id} style={{ background: 'rgba(17,17,24,0.7)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 14, padding: 18, opacity: item.is_active ? 1 : 0.5 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, flexWrap: 'wrap' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                        <span style={{ fontSize: 18 }}>{item.type === 'announcement' ? '📢' : '🚀'}</span>
                        <div style={{ fontWeight: 700, color: '#fff', fontSize: 15 }}>{item.title}</div>
                        <span className={`badge ${item.is_active ? 'active' : 'inactive'}`} style={{ fontSize: 10 }}>
                          {item.is_active ? 'Aktif' : 'Pasif'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => openAnnouncementModal(item)} className="btn-edit" style={{ fontSize: 12, padding: '6px 12px' }}>✏️ Düzenle</button>
                        <button onClick={() => toggleAnnouncementActive(item)} style={{ padding: '6px 12px', background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#9CA3AF', cursor: 'pointer', fontSize: 12 }}>
                          {item.is_active ? 'Pasife Al' : 'Aktif Et'}
                        </button>
                        <button onClick={() => deleteAnnouncement(item.id)} className="btn-delete" style={{ fontSize: 12, padding: '6px 12px' }}>🗑</button>
                      </div>
                    </div>
                    <p style={{ marginTop: 10, marginBottom: 4, color: '#9CA3AF', fontSize: 13, whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>{item.content}</p>
                    <div style={{ fontSize: 11, color: '#6B7280' }}>{new Date(item.created_at).toLocaleString('tr-TR')}</div>
                  </div>
                ))}
              </div>
            )}

            {announcementModal.show && (
              <div className="modal-overlay" onClick={() => setAnnouncementModal({ show: false, item: null })}>
                <div className="modal" style={{ maxWidth: 560 }} onClick={e => e.stopPropagation()}>
                  <div className="modal-header">
                    <h3 className="modal-title">{announcementModal.item ? 'Duyuruyu Düzenle' : 'Yeni Duyuru'}</h3>
                    <button className="modal-close" onClick={() => setAnnouncementModal({ show: false, item: null })}>×</button>
                  </div>
                  <div className="modal-body">
                    <div className="form-group">
                      <label className="form-label">Tür</label>
                      <select className="select" value={announcementForm.type} onChange={e => setAnnouncementForm({ ...announcementForm, type: e.target.value })}>
                        <option value="update">🚀 Güncelleme</option>
                        <option value="announcement">📢 Duyuru</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label className="form-label">Başlık</label>
                      <input className="input" value={announcementForm.title} onChange={e => setAnnouncementForm({ ...announcementForm, title: e.target.value })} placeholder="Örn: Kampanyalar modülü eklendi" />
                    </div>
                    <div className="form-group">
                      <label className="form-label">İçerik</label>
                      <textarea className="textarea" rows={5} value={announcementForm.content} onChange={e => setAnnouncementForm({ ...announcementForm, content: e.target.value })} placeholder="Neler değişti, nasıl kullanılır..." />
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button className="btn btn-secondary" onClick={() => setAnnouncementModal({ show: false, item: null })}>Vazgeç</button>
                    <button className="btn btn-primary" onClick={saveAnnouncement} disabled={announcementSaving}>{announcementSaving ? 'Kaydediliyor...' : 'Kaydet'}</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'subscriptions' && isPageEnabled('subscriptions') && (
          <div>
            <div className="info-box" style={{ marginBottom: 16 }}>
              Şubelerin aylık CRM ödemesini elle takip edin. Vade tarihi geçip <strong>gecikme toleransı</strong>nı
              (varsayılan 7 gün) aşan şubelerin yazma erişimi (yeni randevu/müşteri/satış vb.) otomatik olarak
              kısıtlanır — görüntüleme serbest kalır. Abonelik kaydı hiç oluşturulmamış şubeler bu sistemden
              tamamen etkilenmez.
            </div>
            {subscriptionsLoading ? (
              <div className="loading"><div className="loading-spinner"></div></div>
            ) : (
              <div className="list">
                {subscriptions.length === 0 ? (
                  <div className="empty">Şube bulunamadı</div>
                ) : subscriptions.map(s => {
                  const statusInfo = {
                    active: { label: 'Aktif', cls: 'active' },
                    overdue: { label: `Gecikti (${s.days_overdue} gün)`, cls: 'pending' },
                    suspended: { label: 'Durduruldu', cls: 'inactive' }
                  }[s.status] || { label: 'Takip Edilmiyor', cls: '' };
                  const methodLabel = { bank_transfer: 'Banka Havalesi/EFT', credit_card: 'Kredi Kartı', cash: 'Nakit', other: 'Diğer' }[s.payment_method] || '-';
                  return (
                    <div key={s.branch_id} className="item-card">
                      <div className="item-info">
                        <div className="item-header">
                          <strong>{s.branch_name}</strong>
                          <span className={`badge ${statusInfo.cls}`}>{statusInfo.label}</span>
                          {!s.branch_active && <span className="badge inactive">Şube Pasif</span>}
                        </div>
                        {s.subscription_id ? (
                          <div className="item-details">
                            <span>💰 Aylık {Number(s.monthly_fee).toLocaleString('tr-TR')} ₺</span>
                            <span>💳 {methodLabel}</span>
                            <span>📅 Vade: {s.next_due_date ? new Date(s.next_due_date).toLocaleDateString('tr-TR') : '-'}</span>
                          </div>
                        ) : (
                          <div className="item-details"><span>Bu şube için henüz abonelik kaydı yok.</span></div>
                        )}
                      </div>
                      <div className="item-actions">
                        <button className="btn-edit" onClick={() => setSubscriptionEditModal({
                          branch_id: s.branch_id, branch_name: s.branch_name,
                          monthly_fee: s.monthly_fee || 0, payment_method: s.payment_method || 'bank_transfer',
                          billing_day: s.billing_day || '', next_due_date: s.next_due_date ? String(s.next_due_date).slice(0, 10) : '',
                          grace_period_days: s.grace_period_days || 7, notes: s.notes || ''
                        })}>
                          {s.subscription_id ? 'Düzenle' : 'Abonelik Oluştur'}
                        </button>
                        {s.subscription_id && (<>
                          <button className="btn-ai" onClick={() => setSubscriptionPaymentModal({ branch_id: s.branch_id, branch_name: s.branch_name })}>
                            💰 Ödeme Kaydet
                          </button>
                          <button className="btn-secondary" onClick={() => {
                            setSubscriptionHistoryBranch({ branch_id: s.branch_id, branch_name: s.branch_name });
                            fetchSubscriptionPayments(s.branch_id);
                          }}>
                            🕐 Geçmiş
                          </button>
                          {s.status === 'suspended' ? (
                            <button className="btn-success" onClick={() => handleToggleSubscriptionSuspend(s.branch_id, false)}>Şimdi Aç</button>
                          ) : (
                            <button className="btn-danger" onClick={() => handleToggleSubscriptionSuspend(s.branch_id, true)}>Şimdi Durdur</button>
                          )}
                        </>)}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {subscriptionEditModal && (
              <div className="modal-overlay" onClick={() => setSubscriptionEditModal(null)}>
                <div className="modal" onClick={e => e.stopPropagation()}>
                  <div className="modal-header">
                    <h2>{subscriptionEditModal.branch_name} — Abonelik</h2>
                    <button className="modal-close" onClick={() => setSubscriptionEditModal(null)}>×</button>
                  </div>
                  <form onSubmit={e => { e.preventDefault(); handleSaveSubscription(subscriptionEditModal.branch_id, subscriptionEditModal); }}>
                    <div className="modal-body">
                      <div className="form-group">
                        <label>Aylık Ücret (₺)</label>
                        <input type="number" step="0.01" min="0" className="input" value={subscriptionEditModal.monthly_fee}
                          onChange={e => setSubscriptionEditModal({ ...subscriptionEditModal, monthly_fee: e.target.value })} required />
                      </div>
                      <div className="form-group">
                        <label>Ödeme Yöntemi</label>
                        <select className="input" value={subscriptionEditModal.payment_method}
                          onChange={e => setSubscriptionEditModal({ ...subscriptionEditModal, payment_method: e.target.value })}>
                          <option value="bank_transfer">Banka Havalesi/EFT</option>
                          <option value="credit_card">Kredi Kartı</option>
                          <option value="cash">Nakit</option>
                          <option value="other">Diğer</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Vade Tarihi</label>
                        <input type="date" className="input" value={subscriptionEditModal.next_due_date}
                          onChange={e => setSubscriptionEditModal({ ...subscriptionEditModal, next_due_date: e.target.value })} required />
                      </div>
                      <div className="form-group">
                        <label>Gecikme Toleransı (gün)</label>
                        <input type="number" min="0" className="input" value={subscriptionEditModal.grace_period_days}
                          onChange={e => setSubscriptionEditModal({ ...subscriptionEditModal, grace_period_days: e.target.value })} />
                      </div>
                      <div className="form-group">
                        <label>Not</label>
                        <textarea className="textarea" value={subscriptionEditModal.notes}
                          onChange={e => setSubscriptionEditModal({ ...subscriptionEditModal, notes: e.target.value })} />
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button type="button" className="btn-secondary" onClick={() => setSubscriptionEditModal(null)}>İptal</button>
                      <button type="submit" className="btn-primary">Kaydet</button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {subscriptionPaymentModal && (
              <div className="modal-overlay" onClick={() => setSubscriptionPaymentModal(null)}>
                <div className="modal" onClick={e => e.stopPropagation()}>
                  <div className="modal-header">
                    <h2>{subscriptionPaymentModal.branch_name} — Ödeme Kaydet</h2>
                    <button className="modal-close" onClick={() => setSubscriptionPaymentModal(null)}>×</button>
                  </div>
                  <form onSubmit={e => {
                    e.preventDefault();
                    const f = e.target;
                    handleRecordPayment(subscriptionPaymentModal.branch_id, {
                      amount: f.amount.value, payment_method: f.payment_method.value,
                      payment_date: f.payment_date.value, notes: f.notes.value
                    });
                  }}>
                    <div className="modal-body">
                      <div className="form-group">
                        <label>Tutar (₺)</label>
                        <input type="number" step="0.01" min="0" name="amount" className="input" required />
                      </div>
                      <div className="form-group">
                        <label>Ödeme Yöntemi</label>
                        <select name="payment_method" className="input" defaultValue="bank_transfer">
                          <option value="bank_transfer">Banka Havalesi/EFT</option>
                          <option value="credit_card">Kredi Kartı</option>
                          <option value="cash">Nakit</option>
                          <option value="other">Diğer</option>
                        </select>
                      </div>
                      <div className="form-group">
                        <label>Ödeme Tarihi</label>
                        <input type="date" name="payment_date" className="input" defaultValue={new Date().toISOString().slice(0, 10)} required />
                      </div>
                      <div className="form-group">
                        <label>Not</label>
                        <textarea name="notes" className="textarea" />
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button type="button" className="btn-secondary" onClick={() => setSubscriptionPaymentModal(null)}>İptal</button>
                      <button type="submit" className="btn-primary">Kaydet — vadeyi 1 ay ilerlet</button>
                    </div>
                  </form>
                </div>
              </div>
            )}

            {subscriptionHistoryBranch && (
              <div className="modal-overlay" onClick={() => setSubscriptionHistoryBranch(null)}>
                <div className="modal" onClick={e => e.stopPropagation()}>
                  <div className="modal-header">
                    <h2>{subscriptionHistoryBranch.branch_name} — Ödeme Geçmişi</h2>
                    <button className="modal-close" onClick={() => setSubscriptionHistoryBranch(null)}>×</button>
                  </div>
                  <div className="modal-body">
                    {subscriptionPayments.length === 0 ? (
                      <div className="empty">Henüz ödeme kaydı yok</div>
                    ) : (
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead>
                            <tr>
                              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Tarih</th>
                              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Tutar</th>
                              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Yöntem</th>
                              <th style={{ textAlign: 'left', padding: '6px 8px' }}>Kaydeden</th>
                            </tr>
                          </thead>
                          <tbody>
                            {subscriptionPayments.map(p => (
                              <tr key={p.id}>
                                <td style={{ padding: '6px 8px' }}>{new Date(p.payment_date).toLocaleDateString('tr-TR')}</td>
                                <td style={{ padding: '6px 8px' }}>{Number(p.amount).toLocaleString('tr-TR')} ₺</td>
                                <td style={{ padding: '6px 8px' }}>{{ bank_transfer: 'Banka Havalesi/EFT', credit_card: 'Kredi Kartı', cash: 'Nakit', other: 'Diğer' }[p.payment_method] || '-'}</td>
                                <td style={{ padding: '6px 8px' }}>{p.recorded_by_username || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'custom_companies' && isPageEnabled('custom_companies') && (
          <div style={{ marginTop: '20px' }}>
            {selectedCustomCompanyId ? (
              <CompanyDetail id={selectedCustomCompanyId} onBack={() => setSelectedCustomCompanyId(null)} />
            ) : (
              <CompanyList onManage={(id) => setSelectedCustomCompanyId(id)} />
            )}
          </div>
        )}
        {activeTab === 'call_center' && isPageEnabled('call_center') && (
          <div style={{ marginTop: '20px' }}>
            <CallCenterBoard />
          </div>
        )}
      </div>

      {/* Agent Oluştur/Düzenle Modal */}
      {agentModal.show && (
        <div className="modal-overlay" onClick={() => setAgentModal({ show: false, agent: null })}>
          <div className="modal edit-branch-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <h3>{agentModal.agent ? '✏️ Agent Düzenle' : '🤖 Yeni Agent'}</h3>
            <div className="form-group">
              <label>Agent Adı *</label>
              <input type="text" placeholder="Örn: TR-01 Voice" value={agentForm.name} onChange={e => setAgentForm({ ...agentForm, name: e.target.value })} autoFocus />
            </div>
            <div style={{ display:'flex', gap: 12 }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Tür</label>
                <select value={agentForm.type} onChange={e => setAgentForm({ ...agentForm, type: e.target.value })}>
                  <option value="voice">🎙️ Voice</option>
                  <option value="chat">💬 Chat</option>
                </select>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Durum</label>
                <select value={agentForm.status} onChange={e => setAgentForm({ ...agentForm, status: e.target.value })}>
                  <option value="active">Aktif</option>
                  <option value="waiting">Bekliyor</option>
                  <option value="disabled">Pasif</option>
                </select>
              </div>
            </div>
            <div style={{ display:'flex', gap: 12 }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Dil</label>
                <select value={agentForm.language} onChange={e => {
                  const lmap = { tr: 'Türkçe', en: 'English', de: 'Deutsch', fr: 'Français', ar: 'العربية' };
                  setAgentForm({ ...agentForm, language: e.target.value, language_label: lmap[e.target.value] || e.target.value });
                }}>
                  <option value="tr">🇹🇷 Türkçe</option>
                  <option value="en">🇬🇧 English</option>
                  <option value="de">🇩🇪 Deutsch</option>
                  <option value="fr">🇫🇷 Français</option>
                  <option value="ar">🇸🇦 العربية</option>
                </select>
              </div>
              <div className="form-group" style={{ flex: 1 }}>
                <label>Çalışma Saatleri</label>
                <input type="text" placeholder="7/24" value={agentForm.working_hours} onChange={e => setAgentForm({ ...agentForm, working_hours: e.target.value })} />
              </div>
            </div>
            <div className="modal-actions" style={{ marginTop: 16 }}>
              <button className="btn-cancel" onClick={() => setAgentModal({ show: false, agent: null })}>İptal</button>
              <button className="btn-approve" onClick={saveAgent}>✅ Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'api' && isPageEnabled('api') && (
        <div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '12px' }}>
            <button className={apiTab === 'keys' ? 'active' : ''} onClick={() => setApiTab('keys')}
              style={{ padding: '8px 16px', border: '1px solid rgba(255,255,255,0.06)', background: apiTab === 'keys' ? 'linear-gradient(135deg,#3B82F6,#2563EB)' : 'rgba(26,26,36,0.5)', color: apiTab === 'keys' ? '#FFFFFF' : '#9CA3AF', borderRadius: '8px', cursor: 'pointer', fontWeight: 500, transition: 'all 0.2s' }}>
              🔑 API Anahtarları
            </button>
            <button className={apiTab === 'stats' ? 'active' : ''} onClick={() => setApiTab('stats')}
              style={{ padding: '8px 16px', border: '1px solid rgba(255,255,255,0.06)', background: apiTab === 'stats' ? 'linear-gradient(135deg,#3B82F6,#2563EB)' : 'rgba(26,26,36,0.5)', color: apiTab === 'stats' ? '#FFFFFF' : '#9CA3AF', borderRadius: '8px', cursor: 'pointer', fontWeight: 500, transition: 'all 0.2s' }}>
              📊 Kullanım
            </button>
            <button className={apiTab === 'rate-limits' ? 'active' : ''} onClick={() => setApiTab('rate-limits')}
              style={{ padding: '8px 16px', border: '1px solid rgba(255,255,255,0.06)', background: apiTab === 'rate-limits' ? 'linear-gradient(135deg,#3B82F6,#2563EB)' : 'rgba(26,26,36,0.5)', color: apiTab === 'rate-limits' ? '#FFFFFF' : '#9CA3AF', borderRadius: '8px', cursor: 'pointer', fontWeight: 500, transition: 'all 0.2s' }}>
              ⚡ Rate Limit
            </button>
            <button className={apiTab === 'versions' ? 'active' : ''} onClick={() => setApiTab('versions')}
              style={{ padding: '8px 16px', border: '1px solid rgba(255,255,255,0.06)', background: apiTab === 'versions' ? 'linear-gradient(135deg,#3B82F6,#2563EB)' : 'rgba(26,26,36,0.5)', color: apiTab === 'versions' ? '#FFFFFF' : '#9CA3AF', borderRadius: '8px', cursor: 'pointer', fontWeight: 500, transition: 'all 0.2s' }}>
              📱 Sürüm
            </button>
            <button className={apiTab === 'reference' ? 'active' : ''} onClick={() => setApiTab('reference')}
              style={{ padding: '8px 16px', border: '1px solid rgba(255,255,255,0.06)', background: apiTab === 'reference' ? 'linear-gradient(135deg,#3B82F6,#2563EB)' : 'rgba(26,26,36,0.5)', color: apiTab === 'reference' ? '#FFFFFF' : '#9CA3AF', borderRadius: '8px', cursor: 'pointer', fontWeight: 500, transition: 'all 0.2s' }}>
              📘 Referans
            </button>
            <button className={apiTab === 'tester' ? 'active' : ''} onClick={() => setApiTab('tester')}
              style={{ padding: '8px 16px', border: '1px solid rgba(255,255,255,0.06)', background: apiTab === 'tester' ? 'linear-gradient(135deg,#10B981,#059669)' : 'rgba(26,26,36,0.5)', color: apiTab === 'tester' ? '#FFFFFF' : '#9CA3AF', borderRadius: '8px', cursor: 'pointer', fontWeight: 500, transition: 'all 0.2s' }}>
              🧪 API Test Et
            </button>
            <button className={apiTab === 'webhooks' ? 'active' : ''} onClick={() => setApiTab('webhooks')}
              style={{ padding: '8px 16px', border: '1px solid rgba(255,255,255,0.06)', background: apiTab === 'webhooks' ? 'linear-gradient(135deg,#3B82F6,#2563EB)' : 'rgba(26,26,36,0.5)', color: apiTab === 'webhooks' ? '#FFFFFF' : '#9CA3AF', borderRadius: '8px', cursor: 'pointer', fontWeight: 500, transition: 'all 0.2s' }}>
              🪝 Webhook Logları
            </button>
          </div>

          {apiTab === 'keys' && <ApiKeysTab token={token} />}
          {apiTab === 'stats' && <ApiStatsTab token={token} />}
          {apiTab === 'rate-limits' && <RateLimitsTab token={token} />}
          {apiTab === 'versions' && <MobileVersionsTab token={token} />}
          {apiTab === 'reference' && <ApiReferenceTab token={token} />}
          {apiTab === 'tester' && <ApiTesterTab />}
          {apiTab === 'webhooks' && <WebhookLogsTab token={token} />}
        </div>
      )}

      {activeTab === 'admins' && isPageEnabled('admins') && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 800, margin: 0, background: 'linear-gradient(135deg, #fff, #f59e0b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              👤 Admin Kullanıcıları
            </h2>
            <button className="btn-ai" onClick={() => openAdminUserModal()} style={{ background: 'linear-gradient(135deg, #f59e0b, #d97706)', color: '#fff', border: '1px solid rgba(245,158,11,0.4)' }}>
              ➕ Yeni Admin Ekle
            </button>
          </div>

          {adminUsersLoading ? (
            <div className="admin-loading" style={{ minHeight: '200px' }}>Yükleniyor...</div>
          ) : adminUsers.length === 0 ? (
            <div className="empty">Kayıtlı admin kullanıcısı bulunamadı.</div>
          ) : (
            <div className="list">
              {adminUsers.map(user => (
                <div key={user.id} className="item-card">
                  <div>
                    <div className="item-header">
                      <strong>{user.username}</strong>
                      <span className="badge" style={{ background: 'rgba(245,158,11,0.12)', color: '#fbbf24', borderColor: 'rgba(245,158,11,0.2)' }}>
                        {user.role}
                      </span>
                    </div>
                    <div className="item-details">
                      <span>📧 {user.email || 'E-posta belirtilmemiş'}</span>
                      <span>📅 Kayıt Tarihi: {new Date(user.created_at).toLocaleDateString('tr-TR')}</span>
                      <span>🎨 Markalar: {
                        user.role === 'super_admin' || !Array.isArray(user.permissions) || user.permissions.length === 0
                          ? 'Tümü'
                          : (user.permissions.filter(p => String(p).startsWith('brand:')).map(p => p.slice(6)).join(', ') || 'Tümü (marka atanmamış)')
                      }</span>
                    </div>
                  </div>
                  <div className="item-actions">
                    <button className="btn-edit" onClick={() => openAdminUserModal(user)}>Düzenle</button>
                    <button className="btn-delete" onClick={() => deleteAdminUser(user.id)}>Sil</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {adminUserModal.show && (
        <div className="modal-overlay" onClick={() => setAdminUserModal({ show: false, user: null })}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>{adminUserModal.user ? 'Admin Düzenle' : 'Yeni Admin Ekle'}</h3>
            <div className="form-group">
              <label>Kullanıcı Adı</label>
              <input
                type="text"
                value={adminUserForm.username}
                onChange={e => setAdminUserForm({ ...adminUserForm, username: e.target.value })}
                placeholder="username"
              />
            </div>
            <div className="form-group">
              <label>E-posta</label>
              <input
                type="email"
                value={adminUserForm.email}
                onChange={e => setAdminUserForm({ ...adminUserForm, email: e.target.value })}
                placeholder="email@example.com"
              />
            </div>
            <div className="form-group">
              <label>Şifre {adminUserModal.user && <span style={{ fontSize: '11px', color: 'var(--text-muted)' }}>(Değiştirmek istemiyorsanız boş bırakın)</span>}</label>
              <input
                type="password"
                value={adminUserForm.password}
                onChange={e => setAdminUserForm({ ...adminUserForm, password: e.target.value })}
                placeholder="••••••••"
              />
            </div>
            <div className="form-group">
              <label>Rol</label>
              <select
                value={adminUserForm.role}
                onChange={e => setAdminUserForm({ ...adminUserForm, role: e.target.value })}
              >
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>

            {adminUserForm.role !== 'super_admin' ? (
              <div style={{ marginTop: 16, padding: '14px 16px', background: 'rgba(255,255,255,0.03)', borderRadius: 12, border: '1px solid rgba(255,255,255,0.08)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>🔐 Panel Erişim İzinleri</span>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      type="button"
                      style={{ fontSize: 11, color: '#60A5FA', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                      onClick={() => setAdminUserForm({ ...adminUserForm, permissions: [...adminUserForm.permissions.filter(p => p.startsWith('brand:')), ...ADMIN_PAGES.map(p => p.key)] })}
                    >Tümünü Seç</button>
                    <span style={{ color: 'rgba(255,255,255,0.15)' }}>|</span>
                    <button
                      type="button"
                      style={{ fontSize: 11, color: '#ef4444', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600 }}
                      onClick={() => setAdminUserForm({ ...adminUserForm, permissions: adminUserForm.permissions.filter(p => p.startsWith('brand:')) })}
                    >Temizle</button>
                  </div>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                  {ADMIN_PAGES.map(page => (
                    <label key={page.key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '6px 10px', borderRadius: 8, background: adminUserForm.permissions.includes(page.key) ? 'rgba(245,158,11,0.15)' : 'rgba(255,255,255,0.03)', border: '1px solid', borderColor: adminUserForm.permissions.includes(page.key) ? 'rgba(245,158,11,0.4)' : 'rgba(255,255,255,0.08)', transition: 'all .15s' }}>
                      <input
                        type="checkbox"
                        checked={adminUserForm.permissions.includes(page.key)}
                        onChange={e => {
                          setAdminUserForm(prev => ({
                            ...prev,
                            permissions: e.target.checked
                              ? [...prev.permissions, page.key]
                              : prev.permissions.filter(k => k !== page.key)
                          }));
                        }}
                        style={{ width: 15, height: 15, accentColor: '#f59e0b' }}
                      />
                      <span style={{ fontSize: 13, fontWeight: 500, color: adminUserForm.permissions.includes(page.key) ? '#fbbf24' : '#E5E7EB' }}>{page.label}</span>
                    </label>
                  ))}
                </div>
                <p style={{ margin: '8px 0 0', fontSize: 11, color: '#9CA3AF' }}>
                  Hiçbir sayfa seçilmezse (boş bırakılırsa) bu admin tüm sayfalara erişebilir.
                </p>

                {brandList.length > 0 && (
                  <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>🎨 Marka erişimi</span>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}>
                      {brandList.map(b => {
                        const permKey = `brand:${b.key}`;
                        const checked = adminUserForm.permissions.includes(permKey);
                        return (
                          <label key={b.key} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '6px 10px', borderRadius: 8, background: checked ? 'rgba(245,158,11,0.15)' : 'rgba(255,255,255,0.03)', border: '1px solid', borderColor: checked ? 'rgba(245,158,11,0.4)' : 'rgba(255,255,255,0.08)' }}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={e => {
                                setAdminUserForm(prev => ({
                                  ...prev,
                                  permissions: e.target.checked
                                    ? [...prev.permissions, permKey]
                                    : prev.permissions.filter(k => k !== permKey)
                                }));
                              }}
                              style={{ width: 15, height: 15, accentColor: '#f59e0b' }}
                            />
                            <span style={{ fontSize: 13, fontWeight: 500, color: checked ? '#fbbf24' : '#E5E7EB' }}>
                              {b.label}
                              <span style={{ display: 'block', fontSize: 10.5, color: '#9CA3AF', fontWeight: 400 }}>{(b.hosts || []).filter(h => h.includes('.') && h !== '127.0.0.1')[0]}</span>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    <p style={{ margin: '8px 0 0', fontSize: 11, color: '#9CA3AF' }}>
                      Marka işaretlenirse bu admin firma listelerinde, panel özetinde, aboneliklerde ve
                      raporlarda SADECE işaretli markaların firmalarını görür. Hiç marka işaretlenmezse
                      tüm firmaları görür. "Marka Ayarları" sayfası da seçiliyse işaretli markaların
                      logo/renklerini düzenleyebilir.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div style={{ marginTop: 16, padding: '10px 14px', background: 'rgba(16,185,129,0.1)', borderRadius: 10, border: '1px solid rgba(16,185,129,0.2)', fontSize: 13, color: '#34d399' }}>
                ✅ <strong>Super Admin</strong> rolü tüm panellere ve tüm markalara otomatik olarak erişebilir.
              </div>
            )}

            <div className="modal-actions" style={{ marginTop: 16 }}>
              <button className="btn-cancel" onClick={() => setAdminUserModal({ show: false, user: null })}>İptal</button>
              <button className="btn-approve" onClick={saveAdminUser} style={{ background: 'rgba(245,158,11,0.15)', color: '#fbbf24', border: '1px solid rgba(245,158,11,0.2)' }}>
                ✅ Kaydet
              </button>
            </div>
          </div>
        </div>
      )}

      {editBranchModal.show && (
        <div className="modal-overlay" onClick={() => setEditBranchModal({ show: false, branch: null })}>
          <div className="modal edit-branch-modal" onClick={e => e.stopPropagation()}>
            <h3>Şubeyi Düzenle</h3>
            <div className="form-group">
              <label>Şube Adı</label>
              <input
                type="text"
                value={editForm.name}
                onChange={e => setEditForm({ ...editForm, name: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Şehir</label>
              <input
                type="text"
                value={editForm.city}
                onChange={e => setEditForm({ ...editForm, city: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Adres</label>
              <textarea
                rows={3}
                value={editForm.address}
                onChange={e => setEditForm({ ...editForm, address: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label>Sektör (Sektör Değiştirme)</label>
              <select
                  value={editForm.industry}
                  onChange={e => setEditForm({ ...editForm, industry: e.target.value })}
                >
                  <option value="">Sektör Seçin</option>
                  {industrySettings.map(ind => (
                    <option key={ind.industry} value={ind.industry}>
                      {ind.display_name}
                    </option>
                  ))}
                </select>
            </div>
            <div className="modal-actions" style={{ marginTop: '20px' }}>
              <button className="btn-cancel" onClick={() => setEditBranchModal({ show: false, branch: null })}>İptal</button>
              <button className="btn-approve" onClick={submitEditBranch}>Kaydet</button>
            </div>
          </div>
        </div>
      )}

            {/* Yeni Sektör Ekle Modal */}
      {newIndustryModal && (
        <div className="modal-overlay" onClick={() => setNewIndustryModal(false)}>
          <div className="modal edit-branch-modal" onClick={e => e.stopPropagation()} style={{ width: '520px', maxHeight: '90vh', overflowY: 'auto' }}>
            <h3>Yeni Sektör Ekle</h3>
            <div className="form-group">
              <label>Sektör Anahtarı (Slug - Benzersiz ve Küçük Harf)*</label>
              <input 
                type="text" 
                placeholder="Örn: gym, real_estate" 
                value={industryForm.industry} 
                onChange={e => setIndustryForm({ ...industryForm, industry: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })} 
                required 
              />
            </div>
            <div className="form-group">
              <label>Sektör Görünen Adı *</label>
              <input 
                type="text" 
                placeholder="Örn: Spor Salonu" 
                value={industryForm.display_name} 
                onChange={e => setIndustryForm({ ...industryForm, display_name: e.target.value })} 
                required 
              />
            </div>
            <div className="form-group">
              <label>Sektör Açıklaması</label>
              <textarea 
                placeholder="Örn: Fitness ve spor salonu işletmeleri için" 
                value={industryForm.description} 
                onChange={e => setIndustryForm({ ...industryForm, description: e.target.value })}
                style={{ width: '100%', minHeight: '60px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff', padding: '8px', marginTop: '4px' }}
              />
            </div>
            
            <div className="form-group">
              <label style={{ marginBottom: '8px', display: 'block' }}>Aktif Modüller</label>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', maxHeight: '200px', overflowY: 'auto', background: 'rgba(0,0,0,0.2)', padding: '10px', borderRadius: '8px' }}>
                {Object.entries(MODULE_DEFINITIONS).map(([key, def]) => (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#E5E7EB', cursor: 'pointer' }}>
                    <input 
                      type="checkbox" 
                      checked={!!industryForm.modules[key]} 
                      onChange={e => {
                        const updated = { ...industryForm.modules, [key]: e.target.checked };
                        setIndustryForm({ ...industryForm, modules: updated });
                      }}
                    />
                    {def.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="modal-actions" style={{ marginTop: '20px' }}>
              <button className="btn-cancel" onClick={() => setNewIndustryModal(false)}>İptal</button>
              <button className="btn-approve" onClick={handleSaveIndustry} disabled={!industryForm.industry || !industryForm.display_name}>Ekle</button>
            </div>
          </div>
        </div>
      )}

      {rejectModal.show && (
        <div className="modal-overlay" onClick={() => setRejectModal({ show: false, id: null, type: '' })}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Reddet</h3>
            <p>Bu talebi reddetmek istediğinizden emin misiniz?</p>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={() => setRejectModal({ show: false, id: null, type: '' })}>İptal</button>
              <button className="btn-confirm-reject" onClick={handleReject}>Reddet</button>
            </div>
          </div>
        </div>
      )}

      {automationTemplateModal.show && (
        <div className="modal-overlay" onClick={() => setAutomationTemplateModal({ show: false, template: null, industry: '' })}>
          <div className="modal edit-branch-modal" onClick={e => e.stopPropagation()} style={{ width: '500px' }}>
            <h3>{automationTemplateModal.template ? 'Şablonu Düzenle' : 'Yeni Şablon Ekle'}</h3>
            <div className="form-group">
              <label>Şablon Adı *</label>
              <input 
                type="text" 
                placeholder="Örn: Randevu Hatırlatma (24 Saat)" 
                value={automationTemplateForm.name} 
                onChange={e => setAutomationTemplateForm({ ...automationTemplateForm, name: e.target.value })} 
                required 
              />
            </div>
            <div className="form-group">
              <label>Tetikleyici Tipi</label>
              <select 
                value={automationTemplateForm.trigger_type} 
                onChange={e => setAutomationTemplateForm({ ...automationTemplateForm, trigger_type: e.target.value })}
              >
                <option value="appointment_before">Randevu Öncesi</option>
                <option value="birthday">Doğum Günü</option>
                <option value="no_visit">Ziyaret Etmeyen</option>
                <option value="new_order">Yeni Sipariş</option>
                <option value="payment_reminder">Ödeme Hatırlatma</option>
                <option value="package_ending">Paket Bitiyor</option>
              </select>
            </div>
            {['appointment_before', 'no_visit', 'payment_reminder', 'package_ending'].includes(automationTemplateForm.trigger_type) && (
              <div className="form-group">
                <label>Tetikleyici Değeri (Saat veya Gün Olarak)</label>
                <input 
                  type="number" 
                  value={automationTemplateForm.trigger_value || ''} 
                  onChange={e => setAutomationTemplateForm({ ...automationTemplateForm, trigger_value: e.target.value ? parseInt(e.target.value) : null })} 
                />
                <span style={{ fontSize: '11px', color: '#9CA3AF' }}>
                  {automationTemplateForm.trigger_type === 'appointment_before' && 'Randevudan kaç saat önce gönderileceğini girin. Geri bildirim/anket için negatif değer girilebilir (Örn: -2 randevudan 2 saat sonra).'}
                  {automationTemplateForm.trigger_type === 'no_visit' && 'Son ziyaretten kaç gün sonra gönderileceğini girin (Örn: 30).'}
                  {automationTemplateForm.trigger_type === 'payment_reminder' && 'Taksit tarihinden kaç saat önce gönderileceğini girin.'}
                  {automationTemplateForm.trigger_type === 'package_ending' && 'Pakette son kaç adet kalınca gönderileceğini girin (Örn: 1).'}
                </span>
              </div>
            )}
            <div className="form-group">
              <label>Kanal</label>
              <select 
                value={automationTemplateForm.channel} 
                onChange={e => setAutomationTemplateForm({ ...automationTemplateForm, channel: e.target.value })}
              >
                <option value="whatsapp">WhatsApp</option>
                <option value="sms">SMS</option>
                <option value="email">Email</option>
                <option value="all">Tümü</option>
              </select>
            </div>
            <div className="form-group">
              <label>Mesaj Şablonu İçeriği *</label>
              <textarea 
                rows="4"
                placeholder="Örn: Merhaba {musteri_adi}, yarınki randevunuzu hatırlatmak isteriz."
                value={automationTemplateForm.template} 
                onChange={e => setAutomationTemplateForm({ ...automationTemplateForm, template: e.target.value })} 
                required 
              />
              <span style={{ fontSize: '11px', color: '#9CA3AF', marginTop: '4px' }}>
                Kullanılabilecek değişkenler: <code>{"{musteri_adi}"}</code>, <code>{"{randevu_saati}"}</code>, <code>{"{randevu_tarihi}"}</code>, <code>{"{siparis_no}"}</code>
              </span>
            </div>
            <div className="modal-actions" style={{ marginTop: '20px' }}>
              <button className="btn-cancel" onClick={() => setAutomationTemplateModal({ show: false, template: null, industry: '' })}>İptal</button>
              <button className="btn-approve" onClick={handleSaveAutomationTemplate} disabled={!automationTemplateForm.name || !automationTemplateForm.template}>Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* ── Demo Hesap Oluşturma Modalı ── */}
      {createDemoModal && (
        <div className="modal-overlay" onClick={() => !demoCreating && setCreateDemoModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ width: '440px' }}>
            <h3>🎭 Demo Hesap Oluştur</h3>
            <p style={{ fontSize: '13px', color: '#9CA3AF', marginBottom: '16px' }}>
              Seçilen sektör için tüm verileri (müşteri, randevu, mesaj, paket, ödeme) dolu çalışan demo hesap oluşturulur.
            </p>
            <div className="form-group">
              <label>Sektör Seçin *</label>
              <select
                value={selectedDemoIndustry}
                onChange={e => setSelectedDemoIndustry(e.target.value)}
                disabled={demoCreating}
              >
                {industrySettings.length > 0 ? (
                  industrySettings.map(ind => (
                    <option key={ind.industry} value={ind.industry}>
                      {ind.display_name} ({ind.industry})
                    </option>
                  ))
                ) : (
                  <>
                    <option value="beauty_salon">Güzellik Salonu</option>
                    <option value="dental_clinic">Diş Hekimliği</option>
                    <option value="hotel">Otelcilik</option>
                    <option value="health_tourism">Sağlık Turizmi</option>
                    <option value="wedding_hall">Düğün Salonu</option>
                    <option value="bridal_shop">Gelinlikçi</option>
                    <option value="dusakabin">Duşakabin</option>
                    <option value="clinic">Klinik & Hastane</option>
                    <option value="ecommerce">E-ticaret</option>
                    <option value="automotive">Oto Servis & Otomotiv</option>
                    <option value="veterinary">Veteriner & Pet Klinik</option>
                  </>
                )}
              </select>
            </div>
            <div className="modal-actions" style={{ marginTop: '20px' }}>
              <button className="btn-cancel" onClick={() => setCreateDemoModal(false)} disabled={demoCreating}>
                İptal
              </button>
              <button className="btn-approve" onClick={handleCreateDemoAccount} disabled={demoCreating} style={{ backgroundColor: 'var(--tertiary)', color: '#fff' }}>
                {demoCreating ? 'Oluşturuluyor...' : '🚀 Oluştur'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Demo Hesap Sonuç Modalı ── */}
      {demoResultModal && (
        <div className="modal-overlay" onClick={() => setDemoResultModal(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ width: '480px' }}>
            <h3 style={{ color: '#34D399' }}>✅ Demo Hesap Oluşturuldu!</h3>
            <p style={{ fontSize: '13px', color: '#9CA3AF', marginBottom: '16px' }}>
              Satış ekibi aşağıdaki bilgileri kullanarak sisteme giriş yapabilir:
            </p>
            <div className="info-box" style={{ background: 'rgba(var(--tertiary-rgb),0.12)', border: '1px solid rgba(var(--tertiary-rgb),0.3)', color: '#fff', display: 'flex', flexDirection: 'column', gap: '8px', padding: '14px', borderRadius: '10px' }}>
              <div><strong>Şube / İşletme:</strong> {demoResultModal.branch_name}</div>
              <div><strong>Sektör:</strong> {demoResultModal.industry}</div>
              <div><strong>Giriş E-postası:</strong> <code style={{ color: '#a78bfa', fontSize: '14px' }}>{demoResultModal.login?.email}</code></div>
              <div><strong>Şifre:</strong> <code style={{ color: '#34D399', fontSize: '14px' }}>{demoResultModal.login?.password}</code></div>
            </div>
            <div className="modal-actions" style={{ marginTop: '20px' }}>
              <button className="btn-approve" onClick={() => {
                navigator.clipboard.writeText(`Email: ${demoResultModal.login?.email}\nŞifre: ${demoResultModal.login?.password}`);
                alert('Giriş bilgileri kopyalandı!');
              }}>
                📋 Bilgileri Kopyala
              </button>
              <button className="btn-cancel" onClick={() => setDemoResultModal(null)}>
                Kapat
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .admin-panel { min-height: 100vh; background: transparent; }
        .admin-header { background: rgba(17,17,24,0.8); padding: 16px 24px; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid rgba(255,255,255,0.06); }
        .admin-header h1 { margin: 0; font-size: 20px; color: #FFFFFF; }
        .logout-btn { padding: 8px 16px; background: rgba(239,68,68,0.15); color: #FCA5A5; border: 1px solid rgba(239,68,68,0.2); border-radius: 8px; cursor: pointer; font-weight: 500; }
        .demo-btn { padding: 8px 16px; background: rgba(var(--primary-rgb),0.12); color: var(--primary); border: 1px solid rgba(var(--primary-rgb),0.2); border-radius: 8px; cursor: pointer; font-weight: 500; }
        .demo-btn:hover { background: rgba(var(--primary-rgb),0.2); }
        .admin-content { padding: 24px; padding-bottom: calc(88px + env(safe-area-inset-bottom, 0px)); }

        /* Alt ikonlu gezinme çubuğu - hem mobil hem masaüstünde sabit, tenant
           CRM'in mobil alt navigasyonuyla (Sidebar.js) aynı görsel dilde. */
        .admin-bottom-nav {
          position: fixed;
          bottom: 0;
          left: 0;
          right: 0;
          z-index: 200;
          display: flex;
          background: rgba(3, 4, 7, 0.97);
          border-top: 1px solid rgba(255,255,255,0.08);
          backdrop-filter: blur(24px) saturate(180%);
          padding-bottom: env(safe-area-inset-bottom, 0px);
          box-shadow: 0 -8px 32px rgba(0,0,0,0.6);
        }
        .admin-nav-tab {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 4px;
          padding: 10px 4px;
          color: rgba(255,255,255,0.4);
          background: none;
          border: none;
          cursor: pointer;
          font-family: inherit;
          transition: color 0.2s ease;
          min-width: 0;
        }
        .admin-nav-icon-wrap { position: relative; display: inline-flex; }
        .admin-nav-icon { font-size: 21px; line-height: 1; filter: grayscale(60%); transition: filter 0.2s ease; }
        .admin-nav-tab-active .admin-nav-icon { filter: grayscale(0%); }
        .admin-nav-badge {
          position: absolute; top: -6px; right: -10px;
          background: #ef4444; color: #fff; font-size: 9px; font-weight: 700;
          border-radius: 8px; padding: 1px 4px; min-width: 14px; text-align: center; line-height: 1.4;
        }
        .admin-nav-label { font-size: 10px; font-weight: 500; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 100%; }
        .admin-nav-tab-active { color: var(--primary, var(--primary)); }
        .admin-nav-tab-active .admin-nav-label { font-weight: 700; }

        .admin-more-overlay {
          position: fixed; inset: 0; background: rgba(0,0,0,0.7); backdrop-filter: blur(6px);
          z-index: 300; display: flex; align-items: flex-end; justify-content: center;
        }
        .admin-more-drawer {
          width: 100%; max-width: 640px;
          background: rgba(3, 4, 7, 0.98);
          border-top: 1px solid rgba(255,255,255,0.08);
          border-radius: 24px 24px 0 0;
          padding: 20px 16px;
          padding-bottom: calc(24px + env(safe-area-inset-bottom, 0px));
          animation: adminSlideUpDrawer 0.28s cubic-bezier(0.16, 1, 0.3, 1);
        }
        @keyframes adminSlideUpDrawer {
          from { transform: translateY(100%); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
        .admin-more-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(90px, 1fr));
          gap: 8px;
        }
        .admin-more-item {
          display: flex; flex-direction: column; align-items: center; gap: 6px;
          padding: 14px 8px; border-radius: 14px;
          background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06);
          color: rgba(255,255,255,0.6); font-size: 11px; font-weight: 500; text-align: center;
          font-family: inherit; cursor: pointer; transition: all 0.2s ease;
        }
        .admin-more-item .admin-nav-icon { font-size: 22px; filter: none; }
        .admin-more-item:hover, .admin-more-item-active {
          background: rgba(var(--primary-rgb), 0.12); border-color: rgba(var(--primary-rgb), 0.25); color: var(--primary, var(--primary));
        }
        .list { display: flex; flex-direction: column; gap: 12px; }
        .empty { text-align: center; padding: 40px; color: #9CA3AF; }
        .item-card { background: rgba(17,17,24,0.7); border: 1px solid rgba(255,255,255,0.06); padding: 20px; border-radius: 14px; display: flex; justify-content: space-between; align-items: center; backdrop-filter: blur(12px); }
        .item-header { display: flex; align-items: center; gap: 12px; margin-bottom: 8px; }
        .item-header strong { font-size: 16px; color: #FFFFFF; }
        .badge { padding: 4px 10px; border-radius: 999px; font-size: 12px; font-weight: 600; background: rgba(59,130,246,0.12); color: #60A5FA; border: 1px solid rgba(59,130,246,0.2); }
        .badge.bridal_shop { background: rgba(236,72,153,0.1); color: #F9A8D4; border-color: rgba(236,72,153,0.2); }
        .item-details { display: flex; gap: 16px; font-size: 14px; color: #9CA3AF; flex-wrap: wrap; }
        .item-date { font-size: 12px; color: #6B7280; margin-top: 8px; }
        .item-actions { display: flex; gap: 8px; }
        .btn-approve { padding: 8px 16px; background: rgba(16,185,129,0.15); color: #34D399; border: 1px solid rgba(16,185,129,0.2); border-radius: 8px; cursor: pointer; font-weight: 500; }
        .btn-reject { padding: 8px 16px; background: rgba(239,68,68,0.15); color: #FCA5A5; border: 1px solid rgba(239,68,68,0.2); border-radius: 8px; cursor: pointer; font-weight: 500; }
        .btn-toggle { padding: 8px 16px; background: rgba(245,158,11,0.15); color: #FCD34D; border: 1px solid rgba(245,158,11,0.2); border-radius: 8px; cursor: pointer; font-weight: 500; }
        .btn-edit { padding: 8px 16px; background: rgba(59,130,246,0.15); color: #60A5FA; border: 1px solid rgba(59,130,246,0.2); border-radius: 8px; cursor: pointer; font-weight: 500; }
        .btn-delete { padding: 8px 16px; background: rgba(239,68,68,0.15); color: #FCA5A5; border: 1px solid rgba(239,68,68,0.2); border-radius: 8px; cursor: pointer; font-weight: 500; }
        .btn-ai { padding: 8px 16px; background: linear-gradient(135deg,rgba(var(--secondary-rgb),0.2),rgba(var(--tertiary-rgb),0.2)); color: #a78bfa; border: 1px solid rgba(var(--secondary-rgb),0.3); border-radius: 8px; cursor: pointer; font-weight: 600; transition: all 0.2s; }
        .btn-ai:hover { background: linear-gradient(135deg,rgba(var(--secondary-rgb),0.35),rgba(var(--tertiary-rgb),0.35)); box-shadow: 0 4px 12px rgba(var(--secondary-rgb),0.25); }
        .badge.active { background: rgba(16,185,129,0.12); color: #34D399; border-color: rgba(16,185,129,0.2); }
        .badge.inactive { background: rgba(239,68,68,0.12); color: #FCA5A5; border-color: rgba(239,68,68,0.2); }
        .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.75); backdrop-filter: blur(8px); display: flex; align-items: center; justify-content: center; z-index: 1000; }
        .modal { background: rgba(17,17,24,0.95); border: 1px solid rgba(255,255,255,0.1); padding: 24px; border-radius: 20px; width: 400px; box-shadow: 0 24px 48px rgba(0,0,0,0.6); backdrop-filter: blur(40px); }
        .modal h3 { margin: 0 0 12px; color: #FFFFFF; }
        .modal p { color: #9CA3AF; margin: 0 0 20px; }
        .modal-actions { display: flex; gap: 12px; justify-content: flex-end; }
        .btn-cancel { padding: 10px 20px; background: rgba(255,255,255,0.06); color: #E5E7EB; border: 1px solid rgba(255,255,255,0.1); border-radius: 10px; cursor: pointer; font-weight: 500; }
        .btn-confirm-reject { padding: 10px 20px; background: rgba(239,68,68,0.2); color: #FCA5A5; border: 1px solid rgba(239,68,68,0.3); border-radius: 10px; cursor: pointer; font-weight: 500; }
        .admin-loading { display: flex; align-items: center; justify-content: center; height: 100vh; color: #60A5FA; }
        .info-box { background: rgba(59,130,246,0.08); border: 1px solid rgba(59,130,246,0.15); padding: 16px; border-radius: 10px; color: #60A5FA; margin-bottom: 4px; font-size: 14px; }
        .toggle-switch { position: relative; display: inline-block; width: 50px; height: 28px; }
        .toggle-switch input { opacity: 0; width: 0; height: 0; }
        .toggle-slider { position: absolute; cursor: pointer; top: 0; left: 0; right: 0; bottom: 0; background-color: rgba(255,255,255,0.1); transition: .4s; border-radius: 28px; }
        .toggle-slider:before { position: absolute; content: ""; height: 20px; width: 20px; left: 4px; bottom: 4px; background-color: white; transition: .4s; border-radius: 50%; }
        .toggle-switch input:checked + .toggle-slider { background-color: #3B82F6; box-shadow: 0 0 8px rgba(59,130,246,0.4); }
        .toggle-switch input:checked + .toggle-slider:before { transform: translateX(22px); }
        .industry-card { background: rgba(17,17,24,0.7); border: 1px solid rgba(255,255,255,0.06); border-radius: 14px; overflow: hidden; backdrop-filter: blur(12px); margin-bottom: 12px; }
        .industry-header { display: flex; justify-content: space-between; align-items: center; padding: 18px 20px; cursor: pointer; user-select: none; transition: background .15s; }
        .industry-header:hover { background: rgba(59,130,246,0.05); }
        .industry-icon { font-size: 24px; width: 40px; height: 40px; background: rgba(59,130,246,0.12); border-radius: 10px; display: flex; align-items: center; justify-content: center; }
        .module-expand { padding: 0 20px 20px; border-top: 1px solid rgba(255,255,255,0.05); }
        .module-group-label { font-size: 11px; font-weight: 700; color: #6B7280; text-transform: uppercase; letter-spacing: .06em; margin: 16px 0 10px; }
        .modules-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 8px; }
        .module-item { display: flex; align-items: center; justify-content: space-between; padding: 10px 14px; background: rgba(26,26,36,0.5); border-radius: 8px; cursor: pointer; border: 1px solid rgba(255,255,255,0.06); transition: border-color .15s; }
        .module-item:hover { border-color: rgba(59,130,246,0.3); }
        .module-item.exclusive { background: rgba(var(--secondary-rgb),0.06); border-color: rgba(var(--secondary-rgb),0.15); }
        .module-item.exclusive:hover { border-color: rgba(var(--secondary-rgb),0.3); }
        .module-item input { width: 16px; height: 16px; accent-color: #3B82F6; flex-shrink: 0; }
        .module-label { font-size: 13px; color: #E5E7EB; font-weight: 500; }
        .module-status { font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 20px; flex-shrink: 0; }
        .module-status.on { background: rgba(16,185,129,0.12); color: #34D399; }
        .module-status.off { background: rgba(239,68,68,0.12); color: #FCA5A5; }
        .search-bar { position: relative; margin-bottom: 16px; }
        .search-input {
          width: 100%; padding: 12px 16px;
          background: rgba(26,26,36,0.6);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 12px; color: #FFFFFF; font-size: 14px;
          outline: none; transition: all 0.2s;
          box-sizing: border-box;
        }
        .search-input:focus { border-color: rgba(59,130,246,0.5); box-shadow: 0 0 8px rgba(59,130,246,0.2); background: rgba(26,26,36,0.8); }
        .search-input::placeholder { color: #6B7280; }
        .search-clear {
          position: absolute; right: 10px; top: 50%; transform: translateY(-50%);
          background: rgba(255,255,255,0.08); border: none; color: #9CA3AF;
          width: 28px; height: 28px; border-radius: 50%;
          cursor: pointer; display: flex; align-items: center; justify-content: center;
          font-size: 13px; transition: all 0.15s;
        }
        .search-clear:hover { background: rgba(239,68,68,0.2); color: #FCA5A5; }
        .form-group { display: flex; flex-direction: column; gap: 6px; margin-bottom: 12px; align-items: stretch; text-align: left; }
        .form-group label { font-size: 13px; color: #9CA3AF; font-weight: 500; }
        .form-group input, .form-group textarea, .form-group select {
          padding: 10px 14px;
          background: rgba(26,26,36,0.5);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 10px;
          color: #FFFFFF;
          font-size: 14px;
          outline: none;
          transition: all 0.2s;
        }
        .form-group input:focus, .form-group textarea:focus, .form-group select:focus {
          border-color: rgba(59,130,246,0.5);
          background: rgba(26,26,36,0.8);
          box-shadow: 0 0 8px rgba(59,130,246,0.2);
        }
        .edit-branch-modal { width: 460px; }
        .form-group select option { background: #111118; color: #FFFFFF; }

        /* ─── Mobil / WebView uyumluluğu ───────────────────────────────
           Yönetim paneli bir mobil uygulamaya webview ile gömülecek, native
           bir app gibi hissettirmesi gerekiyor: dokunmada gri "flash" olmasın,
           pinch-zoom/yatay taşma olmasın, giriş alanları iOS'ta otomatik zoom
           yapmasın (16px altı font-size tetikliyor), sekme çubuğu taşmasın. */
        .admin-panel, .admin-header, .admin-bottom-nav, .admin-content, button, input, select, textarea {
          -webkit-tap-highlight-color: transparent;
        }
        .admin-panel { overflow-x: hidden; }
        .form-group input, .form-group textarea, .form-group select {
          font-size: 16px;
        }
        .modal { max-width: 92vw; box-sizing: border-box; max-height: 88vh; overflow-y: auto; }
        .edit-branch-modal { max-width: 92vw; box-sizing: border-box; }

        @media (max-width: 768px) {
          .admin-header { padding: 12px 16px; flex-wrap: wrap; gap: 10px; }
          .admin-header h1 { font-size: 17px; }
          .admin-content { padding: 14px; padding-bottom: calc(80px + env(safe-area-inset-bottom, 0px)); }
          .item-card { flex-direction: column; align-items: stretch; gap: 12px; padding: 16px; }
          .item-actions { flex-wrap: wrap; }
          .item-actions button { flex: 1 1 auto; }
          .modal, .edit-branch-modal { width: 92vw; padding: 18px; }
          table { font-size: 13px; }
          .dashboard-two-col-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

export default AdminPanel;
